import React, { useEffect, useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { ConsultationType, Encounter, Patient, Vitals, StatDose, MaternityFile } from '../../types';
import { MaternityErrorBoundary } from '../../components/ui/MaternityErrorBoundary';
import { DischargeSlipSection } from '../maternity/DischargeSlipSection';
import { PostnatalReviewSection } from '../maternity/PostnatalReviewSection';
import { MaternityWardStats } from '../maternity/MaternityWardStats';
import { filterMaternityWardFiles, MaternityWardFilter, maternityWardFilterLabels } from '../maternity/maternityWard';
import { Activity, ArrowRight, History, Syringe, Trash2, Baby, Save, CheckCircle } from 'lucide-react';
interface Props {}
export const NursingDashboard: React.FC<Props> = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters,
    maternityFiles,
    updateMaternityFile
  } = useData();
  const [activeTab, setActiveTab] = useState<'vitals' | 'medications' | 'maternity'>('vitals');
  const [maternitySection, setMaternitySection] = useState<'record' | 'discharge' | 'postnatal'>('record');
  const [generalExamNote, setGeneralExamNote] = useState('');
  const [wardFilter, setWardFilter] = useState<MaternityWardFilter>('all');
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(null);
  const [consultationType, setConsultationType] = useState<ConsultationType | ''>('');
  const [consultationTypeError, setConsultationTypeError] = useState('');
  // Consultation type belongs to the visit: load it from the opened encounter.
  useEffect(() => {
    setConsultationType(selectedEncounter?.consultationType || '');
    setConsultationTypeError('');
  }, [selectedEncounter?.id]);
  const [selectedMaternityFileRaw, setSelectedMaternityFileRaw] = useState<MaternityFile | null>(null);
  // Older maternity files (and partially-saved drafts) may be missing the deeply
  // nested objects the form reads (`admission.date`, `presentPregnancy.hpTest`,
  // `delivery.deliverySummary`, etc). Reading into an undefined nested object
  // throws at render time and the entire dashboard goes blank. Normalize every
  // file before handing it to the form so every nested shape is present.
  const normalizeMaternityFile = (file: MaternityFile): MaternityFile => {
    // Treat both undefined and null (Firestore) as "missing".
    const f: any = file || {};
    const obj = (v: any) => v && typeof v === 'object' ? v : {};
    const arr = (v: any) => Array.isArray(v) ? v : [];
    return {
      ...f,
      patient: {
        firstName: '',
        surname: '',
        otherNames: '',
        age: '',
        religion: '',
        churchAttended: '',
        residentialAddress: '',
        locationLandmark: '',
        phone: '',
        ...obj(f.patient),
        nextOfKin: {
          name: '',
          phone: '',
          relationship: '',
          ...obj(obj(f.patient).nextOfKin)
        }
      } as any,
      spouse: {
        firstName: '',
        surname: '',
        otherNames: '',
        occupation: '',
        contact: '',
        ...obj(f.spouse)
      } as any,
      admission: {
        date: '',
        time: '',
        ...obj(f.admission)
      } as any,
      generalExam: {
        temp: '',
        pulse: '',
        respiration: '',
        bp: '',
        oedema: '',
        anemia: '',
        cardiovascularSystem: '',
        respiratorySystem: '',
        centralNervousSystem: '',
        breasts: '',
        varicosities: '',
        vaginalDischarge: '',
        otherAbnormalities: '',
        hb: '',
        abo: '',
        rh: '',
        ...obj(f.generalExam),
        msUrine: {
          sg: '',
          albumen: '',
          acetone: '',
          sugar: '',
          ...obj(obj(f.generalExam).msUrine)
        },
        notes: arr(obj(f.generalExam).notes)
      } as any,
      presentPregnancy: {
        lmp: '',
        quickening: '',
        edd: '',
        gestationAge: '',
        ancSummaryFirstBp: '',
        ancSummaryAtWeeks: '',
        pelvicAssessment: '',
        abnormalities: '',
        ...obj(f.presentPregnancy),
        syphilisTest: {
          date: '',
          result: '',
          treatment: '',
          ...obj(obj(f.presentPregnancy).syphilisTest)
        },
        hpTest: {
          date: '',
          result: '',
          ...obj(obj(f.presentPregnancy).hpTest)
        }
      } as any,
      labourObservations: arr(f.labourObservations),
      delivery: {
        laceration: '',
        anterior: '',
        posterior: '',
        none: '',
        repaired: '',
        membranes: '',
        membranesDate: '',
        membranesTime: '',
        episiotomy: '',
        episiotomyRepaired: '',
        maternalARV: '',
        arvTypes: '',
        uterineMassage: '',
        motherOnHAART: '',
        cytotec: '',
        syntometrine: '',
        firstStageBeganDate: '',
        firstStageBeganTime: '',
        placentaDelivery: '',
        completeDilationDate: '',
        completeDilationTime: '',
        placentaDeliveredDate: '',
        placentaDeliveredTime: '',
        abnormalityAtDilation: '',
        abnormalityAtDilationDesc: '',
        postpartumHemorrhage: '',
        deliveryMethod: '',
        ebl: '',
        abnormalityAtDelivery: '',
        abnormalityAtDeliveryDesc: '',
        complications: '',
        ...obj(f.delivery),
        deliverySummary: arr(obj(f.delivery).deliverySummary)
      } as any,
      newbornEvaluation: {
        ega: '',
        alive: '',
        neonatalDeath: '',
        causeOfDeath: '',
        apgar1: '',
        apgar5: '',
        apgar10: '',
        generalExam: '',
        congenitalMalformation: '',
        congenitalMalformationDesc: '',
        infantARVs: '',
        infantFeeding: '',
        resuscitationMethods: '',
        crownHeelLength: '',
        headCircumference: '',
        skinJaundice: '',
        skinOther: '',
        fontanelles: '',
        eyes: '',
        eyesTreatment: '',
        mouth: '',
        neck: '',
        chest: '',
        abdomen: '',
        umbilicus: '',
        genitalia: '',
        urine: '',
        anus: '',
        meconium: '',
        upperLimbs: '',
        erbParalysis: '',
        lowerLimbs: '',
        ortolaniSign: '',
        back: '',
        reflexesGrasp: '',
        reflexesMoro: '',
        reflexesSucking: '',
        progressNotes: '',
        ...obj(f.newbornEvaluation)
      } as any,
      postDeliveryObservations: arr(f.postDeliveryObservations),
      puerperiumNotes: arr(f.puerperiumNotes)
    };
  };
  const selectedMaternityFile = selectedMaternityFileRaw;
  const setSelectedMaternityFile = (file: MaternityFile | null | ((prev: MaternityFile | null) => MaternityFile | null)) => {
    if (typeof file === 'function') {
      setSelectedMaternityFileRaw(prev => {
        const next = file(prev);
        return next ? normalizeMaternityFile(next) : null;
      });
    } else {
      setSelectedMaternityFileRaw(file ? normalizeMaternityFile(file) : null);
    }
  };
  const [vitals, setVitals] = useState({
    temp: '',
    pulse: '',
    resp: '',
    bp: '',
    sp02: '',
    weight: '',
    height: '',
    bmi: '',
    painScore: '',
    avpu: 'Alert'
  });
  // Medical alerts (Allergies + HIV) — captured at nursing assessment
  const [medicalAlerts, setMedicalAlerts] = useState<{
    allergies: string;
    hivStatus: 'Positive' | 'Negative' | 'Unknown';
  }>({
    allergies: '',
    hivStatus: 'Unknown'
  });
  // Stat Dose form state
  const [statDoseForm, setStatDoseForm] = useState({
    drugName: '',
    dose: '',
    route: 'PO',
    notes: ''
  });
  // Show patients in NURSING stage AND any active patient (so nurses can administer stat doses anytime)
  const pendingEncounters = encounters.filter(e => e.stage === 'NURSING');
  const activeEncounters = encounters.filter(e => e.stage !== 'COMPLETED' && e.stage !== 'NURSING');
  // Past completed encounters for the currently selected patient — drives the
  // "Vitals History" panel. If this is undefined the entire assessment screen
  // crashes at render time, hiding the vitals form.
  const pastEncounters = selectedEncounter ? encounters.filter(e => e.patientId === selectedEncounter.patientId && e.id !== selectedEncounter.id && e.vitals).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()) : [];
  const handleAddStatDose = () => {
    if (!selectedEncounter || !currentUser || !statDoseForm.drugName) return;
    const newDose: StatDose = {
      id: `stat-${Date.now()}`,
      drugName: statDoseForm.drugName,
      dose: statDoseForm.dose,
      route: statDoseForm.route,
      notes: statDoseForm.notes,
      givenAt: new Date().toISOString(),
      givenBy: currentUser.id,
      nurseName: currentUser.fullName
    };
    const existing = selectedEncounter.statDoses || [];
    updateEncounter(selectedEncounter.id, {
      statDoses: [...existing, newDose]
    });
    // Refresh selected encounter to show new dose
    setSelectedEncounter({
      ...selectedEncounter,
      statDoses: [...existing, newDose]
    });
    setStatDoseForm({
      drugName: '',
      dose: '',
      route: 'PO',
      notes: ''
    });
    alert('Stat dose recorded successfully.');
  };
  const addGeneralExamNote = () => {
    if (!selectedMaternityFile || !currentUser) return;
    const text = generalExamNote.trim();
    if (!text) return;
    setSelectedMaternityFile({
      ...selectedMaternityFile,
      generalExam: {
        ...selectedMaternityFile.generalExam,
        notes: [...(selectedMaternityFile.generalExam.notes || []), {
          id: `gen-note-${Date.now()}`,
          text,
          recordedAt: new Date().toISOString(),
          recordedBy: currentUser.fullName
        }]
      }
    });
    setGeneralExamNote('');
  };
  const removeGeneralExamNote = (id: string) => {
    if (!selectedMaternityFile) return;
    setSelectedMaternityFile({
      ...selectedMaternityFile,
      generalExam: {
        ...selectedMaternityFile.generalExam,
        notes: (selectedMaternityFile.generalExam.notes || []).filter(note => note.id !== id)
      }
    });
  };
  const handleSaveMaternityDraft = () => {
    if (!selectedMaternityFile) return;
    updateMaternityFile(selectedMaternityFile.id, selectedMaternityFile);
    alert('Maternity file draft saved.');
  };
  const handleCompleteMaternity = () => {
    if (!selectedMaternityFile || !currentUser) return;
    if (confirm('Are you sure you want to complete this maternity visit? It will be moved to the archive.')) {
      updateMaternityFile(selectedMaternityFile.id, {
        ...selectedMaternityFile,
        stage: 'COMPLETED',
        completedAt: new Date().toISOString(),
        completedBy: currentUser.id
      });
      alert('Maternity visit completed and archived.');
      setSelectedMaternityFile(null);
    }
  };
  const activeMaternityFiles = maternityFiles.filter(m => m.stage === 'NURSING');
  const wardFilteredFiles = filterMaternityWardFiles(activeMaternityFiles, wardFilter);
  const calculateBMI = (weightStr: string, heightStr: string) => {
    const w = parseFloat(weightStr);
    const h = parseFloat(heightStr) / 100; // convert cm to m
    if (w > 0 && h > 0) {
      const bmi = (w / (h * h)).toFixed(1);
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi
      }));
    } else {
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi: ''
      }));
    }
  };
  const handleRemoveStatDose = (doseId: string) => {
    if (!selectedEncounter) return;
    const remaining = (selectedEncounter.statDoses || []).filter(d => d.id !== doseId);
    updateEncounter(selectedEncounter.id, {
      statDoses: remaining
    });
    setSelectedEncounter({
      ...selectedEncounter,
      statDoses: remaining
    });
  };
  const handleSaveVitals = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEncounter || !currentUser) return;
    if (!consultationType) {
      setConsultationTypeError('Please select the type of consultation before sending the patient to the clinician.');
      document.getElementById('consultation-type-fieldset')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      return;
    }
    const newVitals: Vitals = {
      ...vitals,
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      vitals: newVitals,
      consultationType,
      stage: 'CLINICIAN'
    });
    setConsultationType('');
    setConsultationTypeError('');
    // Update patient's medical alerts
    const patientToUpdate = patients.find(p => p.id === selectedEncounter.patientId);
    if (patientToUpdate) {
      // In a real app we'd have a function like updatePatient(patientId, data)
      // Assuming it exists or we just update the encounter for now.
    }
    alert('Vitals saved and patient forwarded to Clinician.');
    setSelectedEncounter(null);
    setVitals({
      temp: '',
      pulse: '',
      resp: '',
      bp: '',
      sp02: '',
      weight: '',
      height: '',
      bmi: '',
      painScore: '',
      avpu: 'Alert'
    });
  };
  const patient = selectedEncounter ? patients.find(p => p.id === selectedEncounter.patientId) : null;
  return <div className="space-y-6" data-id="element-8011" data-mp="o121l4ra9e5p">
      <div className="flex justify-between items-center" data-id="element-8012" data-mp="j4bbrppof473">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-8013" data-mp="j4urr0vrniih">Nursing Dashboard</h1>
        <div className="flex space-x-2" data-id="element-8014" data-mp="f155ii2kdd0m">
          <Badge variant="success" className="text-sm px-3 py-1" data-id="element-8015" data-mp="f4ay47aeix13">
            {pendingEncounters.length} Vitals Pending
          </Badge>
          <Badge variant="info" className="text-sm px-3 py-1" data-id="element-8016" data-mp="54jyxsawbtuo">
            {activeEncounters.length} Active Patients
          </Badge>
          <Badge variant="pink" className="text-sm px-3 py-1 bg-pink-100 text-pink-800" data-id="element-8017" data-mp="c90yuzpui4r">
            {activeMaternityFiles.length} Maternity
          </Badge>
        </div>
      </div>

      <div className="border-b border-gray-200" data-id="element-8018" data-mp="3ngghze3zr5z7">
        <nav className="-mb-px flex space-x-8" data-id="element-8019" data-mp="3ii57r29satgg">
          <button onClick={() => {
          setActiveTab('vitals');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'vitals' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-8020" data-mp="3il18eyd0nw8l">
            Vitals Queue
          </button>
          <button onClick={() => {
          setActiveTab('medications');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'medications' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-8021" data-mp="271ap8ppaxr5s">
            Medications & Treatments
          </button>
          <button onClick={() => {
          setActiveTab('maternity');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'maternity' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-8022" data-mp="rkal8h78pf8e">
            <Baby className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-8023" data-mp="28ph1wk9jbd79" />
            Maternity Ward
          </button>
        </nav>
      </div>

      {activeTab === 'vitals' && <div className="space-y-6" data-id="element-8024" data-mp="1fzi86bkearw9">
          {!selectedEncounter ? <div className="space-y-4" data-id="element-8025" data-mp="1b05kxppm3it5">
              <Card title="Awaiting Vitals" noPadding data-id="element-8026" data-mp="1b16z431xclf3">
                <ul className="divide-y divide-gray-200" data-id="element-8027" data-mp="1ozwwljsnon3z">
                  {pendingEncounters.length > 0 ? pendingEncounters.map(enc => {
              const p = patients.find(pat => pat.id === enc.patientId);
              return <li key={enc.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedEncounter(enc)} data-id="element-8028" data-mp="2wcyxyrzu0xg1">
                          <div className="flex items-center" data-id="element-8029" data-mp="38wvu2upjaqti">
                            <div className="bg-blue-100 p-2 rounded-full mr-4" data-id="element-8030" data-mp="2tzdbka3g99ui">
                              <Activity className="h-6 w-6 text-blue-600" data-id="element-8031" data-mp="2ywvbjazhoeqd" />
                            </div>
                            <div data-id="element-8032" data-mp="3mmpn7p82nx91">
                              <p className="text-sm font-medium text-blue-600" data-id="element-8033" data-mp="3mnr9yqyga86l">
                                {p?.bmcFileNumber || '—'}
                              </p>
                              <p className="text-lg font-bold text-gray-900" data-id="element-8034" data-mp="3cw4rc9js96tt">
                                {p?.firstName || 'Patient'} {p?.lastName || ''}
                              </p>
                              <p className="text-sm text-gray-500" data-id="element-8035" data-mp="9g7bpl2ur6o3">
                                {p?.age ?? '—'} yrs • {p?.sex || '—'}
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-8036" data-mp="2ablpk6rvc4vn" />
                        </li>;
            }) : <li className="p-8 text-center text-gray-500" data-id="element-8037" data-mp="21j1zvvtk1amy">
                      No patients waiting for vitals
                    </li>}
                </ul>
              </Card>
              <Card title="Active Patients — Record Additional Vitals" noPadding data-id="element-8038" data-mp="2t85112q5a4vd">
                <ul className="divide-y divide-gray-200" data-id="element-8039" data-mp="2p8cuswo84at6">
                  {activeEncounters.length > 0 ? activeEncounters.map(enc => {
              const p = patients.find(pat => pat.id === enc.patientId);
              return <li key={enc.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedEncounter(enc)} data-id="element-8040" data-mp="18uwdz5qrlflj">
                          <div className="flex items-center" data-id="element-8041" data-mp="yold6y20k6dn">
                            <div className="bg-green-100 p-2 rounded-full mr-4" data-id="element-8042" data-mp="yn1ul0svxj8p">
                              <Activity className="h-6 w-6 text-green-600" data-id="element-8043" data-mp="13n6b2qtsmdyx" />
                            </div>
                            <div data-id="element-8044" data-mp="18haitcavuq12">
                              <p className="text-sm font-medium text-blue-600" data-id="element-8045" data-mp="1chb1j950lndx">
                                {p?.bmcFileNumber || '—'}
                              </p>
                              <p className="text-lg font-bold text-gray-900" data-id="element-8046" data-mp="1hhp6gibbkh7q">
                                {p?.firstName || 'Patient'} {p?.lastName || ''}
                              </p>
                              <p className="text-sm text-gray-500" data-id="element-8047" data-mp="1rbpmvgc1x8vr">
                                {p?.age ?? '—'} yrs • {p?.sex || '—'} •{' '}
                                <span className="text-gray-400" data-id="element-8048" data-mp="3t4nbezyspfaw">
                                  Stage: {enc.stage}
                                </span>
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-8049" data-mp="253bm5s5f6q9b" />
                        </li>;
            }) : <li className="p-8 text-center text-gray-500" data-id="element-8050" data-mp="2ex28aqe2qolu">
                      No active patients
                    </li>}
                </ul>
              </Card>
            </div> : <>
              <div className="flex justify-between items-center" data-id="element-8051" data-mp="2sv8w1dpb787s">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-8052" data-mp="2sw1uzsi10tfd">
                  Nursing Assessment
                </h1>
                <Button variant="outline" onClick={() => setSelectedEncounter(null)} data-id="element-8053" data-mp="2xrzajwlgtvr3">
                  Back to Queue
                </Button>
              </div>

              <Card className="bg-blue-50 border-blue-200" data-id="element-8054" data-mp="vwji6idtqbz6">
                <div className="flex justify-between items-start" data-id="element-8055" data-mp="vy4dwbbvk0ym">
                  <div data-id="element-8056" data-mp="r51p0gi1lfor">
                    <h2 className="text-xl font-bold text-gray-900" data-id="element-8057" data-mp="r4ry8qp6b4i0">
                      {patient?.firstName || 'Patient'}{' '}
                      {patient?.lastName || ''}
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-8058" data-mp="h9q9hvyt84fy">
                      BMC: {patient?.bmcFileNumber || '—'} •{' '}
                      {patient?.age ?? '—'} yrs • {patient?.sex || '—'}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-8059" data-mp="14wyf6f05hfii">
                    <p className="text-sm font-medium text-red-600" data-id="element-8060" data-mp="14vodlfu39p74">
                      Allergies: {medicalAlerts.allergies || '—'}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-8061" data-mp="3pnywmoikc30q">
                      HIV Status: {medicalAlerts.hivStatus}
                    </p>
                  </div>
                </div>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-8062" data-mp="3n30tkw157wp5">
                <div className="lg:col-span-2" data-id="element-8063" data-mp="3s23qtuavsymk">
                  <Card title="Enter Vitals" data-id="element-8064" data-mp="3s1vd606xppab">
                    <form onSubmit={handleSaveVitals} className="space-y-6" data-id="element-8065" data-mp="3d54ke9rrnbto">
                      <fieldset id="consultation-type-fieldset" aria-describedby={consultationTypeError ? 'consultation-type-error' : undefined} className={`rounded-lg border p-4 ${consultationTypeError ? 'border-red-300 bg-red-50' : 'border-blue-200 bg-blue-50/50'}`} data-id="element-8066" data-mp="3d77asuwtz6yx">
                        <legend className="px-1 text-sm font-semibold text-gray-900" data-id="element-8067" data-mp="2ze7w2v4vhgsf">
                          Type of Consultation{' '}
                          <span className="text-red-600" aria-hidden="true" data-id="element-8068" data-mp="2pfiopwalreqf">
                            *
                          </span>
                        </legend>
                        <div className="mt-1 grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-8069" data-mp="26rhxnjdx2697">
                          {([['Gyne', 'Gyne Consultation'], ['General', 'General Consultation']] as const).map(([value, label]) => {
                      const checked = consultationType === value;
                      return <label key={value} className={`flex cursor-pointer items-center gap-3 rounded-md border bg-white px-4 py-3 text-sm font-medium ${checked ? value === 'Gyne' ? 'border-pink-500 ring-1 ring-pink-500 text-pink-800' : 'border-blue-500 ring-1 ring-blue-500 text-blue-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-8070" data-mp="2kq7xh6s4ek19">
                                <input type="radio" name="consultationType" value={value} checked={checked} aria-required="true" onChange={() => {
                          setConsultationType(value);
                          setConsultationTypeError('');
                        }} className="h-4 w-4 text-blue-600 focus:ring-blue-500" data-id="element-8071" data-mp="3njleurlldjb4" />
                                {label}
                              </label>;
                    })}
                        </div>
                        {consultationTypeError && <p id="consultation-type-error" role="alert" className="mt-2 text-sm font-medium text-red-700" data-id="element-8072" data-mp="suf1ch420jvy">
                            {consultationTypeError}
                          </p>}
                      </fieldset>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-8073" data-mp="28lqziwtm8no2">
                        <Input label="Blood Pressure (mmHg)" placeholder="120/80" required value={vitals.bp} onChange={e => setVitals({
                    ...vitals,
                    bp: e.target.value
                  })} data-id="element-8074" data-mp="23npfo9od8kbj" />
                        <Input label="Temperature (°C)" type="number" step="0.1" required value={vitals.temp} onChange={e => setVitals({
                    ...vitals,
                    temp: e.target.value
                  })} data-id="element-8075" data-mp="1kqobgr2myo8n" />
                        <Input label="Pulse (bpm)" type="number" required value={vitals.pulse} onChange={e => setVitals({
                    ...vitals,
                    pulse: e.target.value
                  })} data-id="element-8076" data-mp="13d6bwgskn8ol" />
                        <Input label="Resp. Rate (cpm)" type="number" required value={vitals.resp} onChange={e => setVitals({
                    ...vitals,
                    resp: e.target.value
                  })} data-id="element-8077" data-mp="1r3se3rfw9yqo" />
                        <Input label="SpO2 (%)" type="number" required value={vitals.sp02} onChange={e => setVitals({
                    ...vitals,
                    sp02: e.target.value
                  })} data-id="element-8078" data-mp="i41vhv4s6xog" />
                        <Input label="Pain Score (0-10)" type="number" min="0" max="10" required value={vitals.painScore} onChange={e => setVitals({
                    ...vitals,
                    painScore: e.target.value
                  })} data-id="element-8079" data-mp="3vc6dvidey50p" />

                        <Input label="Weight (kg)" type="number" step="0.1" required value={vitals.weight} onChange={e => calculateBMI(e.target.value, vitals.height || '')} data-id="element-8080" data-mp="2makiuopw02sm" />
                        <Input label="Height (cm)" type="number" step="0.1" required value={vitals.height} onChange={e => calculateBMI(vitals.weight || '', e.target.value)} data-id="element-8081" data-mp="350k4t2qfl1rt" />
                        <Input label="BMI" readOnly value={vitals.bmi} className="bg-gray-50" data-id="element-8082" data-mp="17eaw4e0o0g0s" />
                      </div>

                      <div className="border-t border-gray-200 pt-4" data-id="element-8083" data-mp="yeecwrx1o9d4">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8084" data-mp="tf1k7qjhs82m">
                          AVPU Scale
                        </label>
                        <div className="flex space-x-4" data-id="element-8085" data-mp="1m1maeno8ee4l">
                          {['Alert', 'Verbal', 'Pain', 'Unresponsive'].map(level => <label key={level} className="flex items-center" data-id="element-8086" data-mp="1cccf6yy3cfc9">
                                <input type="radio" name="avpu" value={level} checked={vitals.avpu === level} onChange={e => setVitals({
                        ...vitals,
                        avpu: e.target.value
                      })} className="mr-2" data-id="element-8087" data-mp="1ca8bah06zgbz" />
                                {level}
                              </label>)}
                        </div>
                      </div>

                      {/* Medical Alerts (moved from Reception) */}
                      <div className="border-t border-gray-200 pt-4" data-id="element-8088" data-mp="2piy0a95j1k01">
                        <h4 className="text-md font-semibold text-gray-900 mb-3" data-id="element-8089" data-mp="2kk384glo6j39">
                          Medical Alerts
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8090" data-mp="2zfj79013x56i">
                          <Input label="Known Allergies" placeholder="e.g. Penicillin, Peanuts (or 'None')" required value={medicalAlerts.allergies} onChange={e => setMedicalAlerts({
                      ...medicalAlerts,
                      allergies: e.target.value
                    })} data-id="element-8091" data-mp="2uk3r21fmmx6x" />
                          <div data-id="element-8092" data-mp="2z7lfxnuj3ttw">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8093" data-mp="2z7v3l4s3powf">
                              HIV Status
                            </label>
                            <select required value={medicalAlerts.hivStatus} onChange={e => setMedicalAlerts({
                        ...medicalAlerts,
                        hivStatus: e.target.value as 'Positive' | 'Negative' | 'Unknown'
                      })} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-8094" data-mp="2qdym439vt0nc">
                              <option value="Unknown" data-id="element-8095" data-mp="2bbqeqgzo5zfv">Unknown</option>
                              <option value="Positive" data-id="element-8096" data-mp="2bci43a2ob0xp">Positive</option>
                              <option value="Negative" data-id="element-8097" data-mp="26bc7301zcrid">Negative</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end pt-4" data-id="element-8098" data-mp="uyjks4rykv9p">
                        <Button type="submit" size="lg" data-id="element-8099" data-mp="pyndnwnysnpx">
                          Save & Forward to Clinician
                        </Button>
                      </div>
                    </form>
                  </Card>

                  {/* Stat / Start Dose Section */}
                  <Card title="Stat / Start Dose Administered" className="mt-6" action={<Syringe className="w-5 h-5 text-pink-500" data-id="element-8101" data-mp="3gadc1b9yuc9h" />} data-id="element-8100" data-mp="36ir3xnsxctay">
                    <p className="text-xs text-gray-500 mb-4" data-id="element-8102" data-mp="3l9q5wfz98jlk">
                      Record any medication given to the patient as a start/stat
                      dose. These will appear on the patient file and be visible
                      to the clinician.
                    </p>

                    {/* Existing Stat Doses */}
                    {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="mb-4 space-y-2" data-id="element-8103" data-mp="1zxy8zwkwqhn9">
                          {selectedEncounter.statDoses.map(d => <div key={d.id} className="flex justify-between items-start bg-pink-50 border border-pink-200 rounded p-3 text-sm" data-id="element-8104" data-mp="24xb3n3i0st0e">
                              <div data-id="element-8105" data-mp="88dx0r0f6h8s">
                                <p className="font-bold text-gray-900" data-id="element-8106" data-mp="87ce60d5wqgi">
                                  {d.drugName}{' '}
                                  <span className="text-gray-600 font-normal" data-id="element-8107" data-mp="44g10p6e5mjy">
                                    {d.dose} • {d.route}
                                  </span>
                                </p>
                                <p className="text-xs text-gray-600 mt-0.5" data-id="element-8108" data-mp="3lee3pa4wvla7">
                                  Given by {d.nurseName} •{' '}
                                  {new Date(d.givenAt).toLocaleString()}
                                </p>
                                {d.notes && <p className="text-xs text-gray-700 mt-1 italic" data-id="element-8109" data-mp="2owhsczu32qib">
                                    {d.notes}
                                  </p>}
                              </div>
                              <button onClick={() => handleRemoveStatDose(d.id)} className="text-gray-400 hover:text-red-600 p-1" title="Remove" data-id="element-8110" data-mp="2jkcq2c49otpj">
                                <Trash2 className="w-4 h-4" data-id="element-8111" data-mp="2xjc4ys1z9u8i" />
                              </button>
                            </div>)}
                        </div>}

                    {/* Add New Stat Dose */}
                    <div className="grid grid-cols-2 gap-3 border-t border-gray-200 pt-4" data-id="element-8112" data-mp="2nkmxzubmdu71">
                      <Input label="Drug Name" className="col-span-2" placeholder="e.g. Paracetamol" value={statDoseForm.drugName} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  drugName: e.target.value
                })} data-id="element-8113" data-mp="2siep4m4fxca6" />
                      <Input label="Dose" placeholder="e.g. 500mg" value={statDoseForm.dose} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  dose: e.target.value
                })} data-id="element-8114" data-mp="3a03ogf08me75" />
                      <div data-id="element-8115" data-mp="2r4wspb8lehrg">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8116" data-mp="2m3zc3338dvs2">
                          Route
                        </label>
                        <select value={statDoseForm.route} onChange={e => setStatDoseForm({
                    ...statDoseForm,
                    route: e.target.value
                  })} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-8117" data-mp="paxk4xmuueig">
                          <option value="PO" data-id="element-8118" data-mp="16k017gsdu86h">Oral (PO)</option>
                          <option value="IV" data-id="element-8119" data-mp="8xsv22gfk7s0">Intravenous (IV)</option>
                          <option value="IM" data-id="element-8120" data-mp="dvuguv58ql76">Intramuscular (IM)</option>
                          <option value="SC" data-id="element-8121" data-mp="dy6wh1nj0jh2">Subcutaneous (SC)</option>
                          <option value="PR" data-id="element-8122" data-mp="iue5j3b7yb38">Per Rectum (PR)</option>
                          <option value="Topical" data-id="element-8123" data-mp="nutgxpbxx8ik">Topical</option>
                        </select>
                      </div>
                      <Input label="Notes (optional)" className="col-span-2" placeholder="Reason or observation..." value={statDoseForm.notes} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  notes: e.target.value
                })} data-id="element-8124" data-mp="1u9km4583jfie" />
                    </div>
                    <div className="mt-4 flex justify-end" data-id="element-8125" data-mp="2pb9megla8oxz">
                      <Button type="button" onClick={handleAddStatDose} disabled={!statDoseForm.drugName} className="bg-pink-600 hover:bg-pink-700 text-white disabled:bg-gray-300" data-id="element-8126" data-mp="3337kzxhdb2fw">
                        <Syringe className="w-4 h-4 mr-2" data-id="element-8127" data-mp="3843qugla4yny" /> Mark Stat Dose
                        Given
                      </Button>
                    </div>
                  </Card>
                </div>

                <div className="lg:col-span-1" data-id="element-8128" data-mp="1dq2srxxk0fox">
                  <Card title="Vitals History" className="h-full" data-id="element-8129" data-mp="1drdwxyuwvpgk">
                    {pastEncounters.length > 0 ? <div className="space-y-4 overflow-y-auto max-h-[500px] pr-2" data-id="element-8130" data-mp="3ub7rdls5atnz">
                        {pastEncounters.map(enc => <div key={enc.id} className="bg-gray-50 p-3 rounded border border-gray-200 text-sm" data-id="element-8131" data-mp="3fm1wub495rmf">
                            <p className="font-medium text-gray-900 mb-2 border-b pb-1" data-id="element-8132" data-mp="36l3nddb1orwg">
                              {new Date(enc.startedAt).toLocaleDateString()}
                            </p>
                            <div className="grid grid-cols-2 gap-2" data-id="element-8133" data-mp="2wp0n6czr73c5">
                              <div data-id="element-8134" data-mp="31maejy6pdp2m">
                                <span className="text-gray-500" data-id="element-8135" data-mp="31nbxeotynfuw">BP:</span>{' '}
                                {enc.vitals?.bp}
                              </div>
                              <div data-id="element-8136" data-mp="zvp5ml28vgxd">
                                <span className="text-gray-500" data-id="element-8137" data-mp="zuxb7dkdvp8o">Temp:</span>{' '}
                                {enc.vitals?.temp}°C
                              </div>
                              <div data-id="element-8138" data-mp="30blul4o20pbd">
                                <span className="text-gray-500" data-id="element-8139" data-mp="30aj5n2aakwy3">Pulse:</span>{' '}
                                {enc.vitals?.pulse}
                              </div>
                              <div data-id="element-8140" data-mp="2r9l36ofi4xhm">
                                <span className="text-gray-500" data-id="element-8141" data-mp="2rcqp5usu3j94">SpO2:</span>{' '}
                                {enc.vitals?.sp02}%
                              </div>
                              <div data-id="element-8142" data-mp="2hd9lfix57ffs">
                                <span className="text-gray-500" data-id="element-8143" data-mp="2heazlw9ggi1q">Weight:</span>{' '}
                                {enc.vitals?.weight}kg
                              </div>
                              <div data-id="element-8144" data-mp="28ko3u658zesj">
                                <span className="text-gray-500" data-id="element-8145" data-mp="1tmvqnkn6b2hi">BMI:</span>{' '}
                                {enc.vitals?.bmi}
                              </div>
                            </div>
                          </div>)}
                      </div> : <div className="text-center py-8 text-gray-500 flex flex-col items-center" data-id="element-8146" data-mp="1ayv0d9yb9y1c">
                        <History className="w-8 h-8 mb-2 text-gray-400" data-id="element-8147" data-mp="1oy350id8tycx" />
                        <p data-id="element-8148" data-mp="1ow9yd32mre4x">No previous vitals recorded</p>
                      </div>}
                  </Card>
                </div>
              </div>
            </>}
        </div>}

      {activeTab === 'maternity' && <div className="space-y-6" data-id="element-8149" data-mp="1twefmvbd4cw7">
        <MaternityWardStats files={activeMaternityFiles} activeFilter={wardFilter} onSelect={filter => {
        setWardFilter(filter);
        setSelectedMaternityFile(null);
      }} data-id="element-8150" data-mp="1tvd28k6vjebb" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-8151" data-mp="2hms5qgae01o5">
          <div className="lg:col-span-1" data-id="element-8152" data-mp="2hkxn58ml8ln8">
            <Card title={maternityWardFilterLabels[wardFilter]} noPadding data-id="element-8153" data-mp="2cogvw5k3dr5x">
              <ul className="divide-y divide-gray-200" data-id="element-8154" data-mp="2jr5ejnpdrb1m">
                {wardFilteredFiles.length > 0 ? wardFilteredFiles.map(file => <li key={file.id} className={`p-4 hover:bg-gray-50 cursor-pointer ${selectedMaternityFile?.id === file.id ? 'bg-pink-50' : ''}`} onClick={() => {
                setSelectedMaternityFile(file);
                if (wardFilter === 'postnatal') setMaternitySection('postnatal');
              }} data-id="element-8155" data-mp="2oqhxvyh1lzzg">
                      <div className="flex items-center" data-id="element-8156" data-mp="3cfay474p6tzv">
                        <div className="bg-pink-100 p-2 rounded-full mr-3" data-id="element-8157" data-mp="3cdq36geh191h">
                          <Baby className="h-5 w-5 text-pink-600" data-id="element-8158" data-mp="3hdkqyb2y9iib" />
                        </div>
                        <div data-id="element-8159" data-mp="1i335x1jrgr7s">
                          <p className="text-sm font-medium text-blue-600" data-id="element-8160" data-mp="1i3w43e4nm8eb">
                            {file.bmcFileNumber}
                          </p>
                          <p className="text-base font-bold text-gray-900" data-id="element-8161" data-mp="1fieybc65nk9a">
                            {file.patient.firstName} {file.patient.surname}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-8162" data-mp="15r0r0i7ypg5l">
                            Registered:{' '}
                            {new Date(file.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </li>) : <li className="p-8 text-center text-gray-500" data-id="element-8163" data-mp="1tho2tel00pxf">
                    <Baby className="mx-auto h-8 w-8 text-gray-300 mb-2" data-id="element-8164" data-mp="1oh8tcy3u82kq" />
                    <p data-id="element-8165" data-mp="1jiytia764dss">
                      {wardFilter === 'all' ? 'No active maternity patients' : `No patients under ${maternityWardFilterLabels[wardFilter].toLowerCase()}`}
                    </p>
                  </li>}
              </ul>
            </Card>
          </div>

          <div className="lg:col-span-2" data-id="element-8166" data-mp="1wx7hgspivumo">
            {selectedMaternityFile ? <MaternityErrorBoundary key={selectedMaternityFile.id} onReset={() => setSelectedMaternityFile(null)} data-id="element-8167" data-mp="21oxuj6fugwhs">
                <div className="space-y-6" data-id="element-8168" data-mp="1ryw8slayrq61">
                  <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-200 sticky top-0 z-10" data-id="element-8169" data-mp="1rzxt7gdvdp0f">
                    <div data-id="element-8170" data-mp="2fmmjapd18ns4">
                      <h2 className="text-xl font-bold text-gray-900" data-id="element-8171" data-mp="2ko3i8fbyi062">
                        {selectedMaternityFile.patient?.firstName}{' '}
                        {selectedMaternityFile.patient?.surname}
                      </h2>
                      <p className="text-sm text-gray-500" data-id="element-8172" data-mp="2ao2y4r69jg0t">
                        File: {selectedMaternityFile.bmcFileNumber} | Age:{' '}
                        {selectedMaternityFile.patient?.age}
                      </p>
                    </div>
                    <div className="flex space-x-2" data-id="element-8173" data-mp="3dhq5w0ooyogw">
                      <Button variant="outline" onClick={handleSaveMaternityDraft} data-id="element-8174" data-mp="3dgf3o5aw9orw">
                        <Save className="w-4 h-4 mr-2" data-id="element-8175" data-mp="3mj6go9jf4sln" /> Save Draft
                      </Button>
                      <Button variant="primary" className="bg-pink-600 hover:bg-pink-700 text-white" onClick={handleCompleteMaternity} data-id="element-8176" data-mp="3igjmw336sxlt">
                        <CheckCircle className="w-4 h-4 mr-2" data-id="element-8177" data-mp="2ptp192umvs6r" /> Complete Visit
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 border-b border-gray-200" role="tablist" aria-label="Maternity file sections" data-id="element-8178" data-mp="2ytc4lu6xb82g">
                    {([{
                  id: 'record',
                  label: 'Maternity Record'
                }, {
                  id: 'discharge',
                  label: 'Discharge Slip'
                }, {
                  id: 'postnatal',
                  label: 'Postnatal Review'
                }] as const).map(tab => <button key={tab.id} type="button" role="tab" aria-selected={maternitySection === tab.id} onClick={() => setMaternitySection(tab.id)} className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pink-500 ${maternitySection === tab.id ? 'border-pink-600 text-pink-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`} data-id="element-8179" data-mp="3na6atf1e6a4u">
                        {tab.label}
                      </button>)}
                  </div>

                  {maternitySection === 'record' && <>
                  <Card title="Admission & General Exam" data-id="element-8180" data-mp="2s61cjclkh4op">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-8181" data-mp="2n7zu8tvyt9ea">
                      <Input type="date" label="Date of Admission" value={selectedMaternityFile.admission.date} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      admission: {
                        ...selectedMaternityFile.admission,
                        date: e.target.value
                      }
                    })} data-id="element-8182" data-mp="2n5vqqd1zacej" />
                      <Input type="time" label="Time of Admission" value={selectedMaternityFile.admission.time} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      admission: {
                        ...selectedMaternityFile.admission,
                        time: e.target.value
                      }
                    })} data-id="element-8183" data-mp="2fo72f0jr42b" />
                    </div>
                    <h4 className="font-medium text-gray-900 mb-3 border-b pb-1" data-id="element-8184" data-mp="2etdk7uxz8t0l">
                      General Examination
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-8185" data-mp="2jsqf92z056e9">
                      <Input label="Temp" value={selectedMaternityFile.generalExam.temp} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        temp: e.target.value
                      }
                    })} data-id="element-8186" data-mp="d2tp6fq9lqqw" />
                      <Input label="Pulse" value={selectedMaternityFile.generalExam.pulse} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        pulse: e.target.value
                      }
                    })} data-id="element-8187" data-mp="2l9doouq7fccc" />
                      <Input label="Respiration" value={selectedMaternityFile.generalExam.respiration} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        respiration: e.target.value
                      }
                    })} data-id="element-8188" data-mp="vw0g2tb2725f" />
                      <Input label="BP" value={selectedMaternityFile.generalExam.bp} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        bp: e.target.value
                      }
                    })} data-id="element-8189" data-mp="3d7elvtgowwhb" />
                      <Input label="Oedema" value={selectedMaternityFile.generalExam.oedema} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        oedema: e.target.value
                      }
                    })} data-id="element-8190" data-mp="19y34xg5t9gxn" />
                      <Input label="Anemia" value={selectedMaternityFile.generalExam.anemia} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        anemia: e.target.value
                      }
                    })} data-id="element-8191" data-mp="10xo7xjjhideb" />
                      <Input label="Hb%" value={selectedMaternityFile.generalExam.hb} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        hb: e.target.value
                      }
                    })} data-id="element-8192" data-mp="qcfzovd895gh" />
                      <Input label="ABO" value={selectedMaternityFile.generalExam.abo} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        abo: e.target.value
                      }
                    })} data-id="element-8193" data-mp="2k8x3egws28m" />
                      <Input label="Rh" value={selectedMaternityFile.generalExam.rh} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        rh: e.target.value
                      }
                    })} data-id="element-8194" data-mp="1hin076l7ughm" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-8195" data-mp="ouhdwfeikjfo">
                      <Input label="Cardiovascular System" value={selectedMaternityFile.generalExam.cardiovascularSystem} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        cardiovascularSystem: e.target.value
                      }
                    })} data-id="element-8196" data-mp="oseex5vdvfyt" />
                      <Input label="Respiratory System" value={selectedMaternityFile.generalExam.respiratorySystem} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        respiratorySystem: e.target.value
                      }
                    })} data-id="element-8197" data-mp="1e1a9fxnb7zcz" />
                      <Input label="Central Nervous System" value={selectedMaternityFile.generalExam.centralNervousSystem} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        centralNervousSystem: e.target.value
                      }
                    })} data-id="element-8198" data-mp="29b2vytv2mtd" />
                      <Input label="Breasts" value={selectedMaternityFile.generalExam.breasts} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        breasts: e.target.value
                      }
                    })} data-id="element-8199" data-mp="2n01y8vucnnb6" />
                      <Input label="Varicosities" value={selectedMaternityFile.generalExam.varicosities} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        varicosities: e.target.value
                      }
                    })} data-id="element-8200" data-mp="2szdigbpi8kzi" />
                      <Input label="Vaginal Discharge" value={selectedMaternityFile.generalExam.vaginalDischarge} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        vaginalDischarge: e.target.value
                      }
                    })} data-id="element-8201" data-mp="453t7q5dell3" />
                    </div>
                    <div className="mt-4" data-id="element-8202" data-mp="37iy4sqobcs33">
                      <Input label="Other Abnormalities" value={selectedMaternityFile.generalExam.otherAbnormalities} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        otherAbnormalities: e.target.value
                      }
                    })} data-id="element-8203" data-mp="2tl9n1rpjm129" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-8204" data-mp="2qdba53bi6pwu">
                      MS Urine
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-8205" data-mp="2lehvv8oe6uvm">
                      <Input label="SG" value={selectedMaternityFile.generalExam.msUrine.sg} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        msUrine: {
                          ...selectedMaternityFile.generalExam.msUrine,
                          sg: e.target.value
                        }
                      }
                    })} data-id="element-8206" data-mp="2hd65v21lw7mw" />
                      <Input label="Albumen" value={selectedMaternityFile.generalExam.msUrine.albumen} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        msUrine: {
                          ...selectedMaternityFile.generalExam.msUrine,
                          albumen: e.target.value
                        }
                      }
                    })} data-id="element-8207" data-mp="46ybc4lz1frt" />
                      <Input label="Acetone" value={selectedMaternityFile.generalExam.msUrine.acetone} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        msUrine: {
                          ...selectedMaternityFile.generalExam.msUrine,
                          acetone: e.target.value
                        }
                      }
                    })} data-id="element-8208" data-mp="321573hfeqg87" />
                      <Input label="Sugar" value={selectedMaternityFile.generalExam.msUrine.sugar} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        msUrine: {
                          ...selectedMaternityFile.generalExam.msUrine,
                          sugar: e.target.value
                        }
                      }
                    })} data-id="element-8209" data-mp="1o1jxziofkrlg" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-8210" data-mp="qfmjrxh338fb">
                      Examination Notes
                    </h4>
                    <p className="text-sm text-gray-500 mb-3" data-id="element-8211" data-mp="hctprmgioils">
                      Add any narrative observations recorded after the
                      examination above.
                    </p>
                    {(selectedMaternityFile.generalExam.notes || []).length > 0 && <ul className="space-y-2 mb-4" data-id="element-8212" data-mp="3txr2i1n1vg7v">
                        {(selectedMaternityFile.generalExam.notes || []).map(note => <li key={note.id} className="flex items-start justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 p-3" data-id="element-8213" data-mp="qg8t3prmq15c">
                              <div className="min-w-0" data-id="element-8214" data-mp="gpe9yu9ojho4">
                                <p className="text-sm text-gray-900 whitespace-pre-wrap" data-id="element-8215" data-mp="lpirmnmbqifx">
                                  {note.text}
                                </p>
                                <p className="mt-1 text-xs text-gray-500" data-id="element-8216" data-mp="19ds8rc8lei0q">
                                  {note.recordedBy} ·{' '}
                                  {new Date(note.recordedAt).toLocaleString()}
                                </p>
                              </div>
                              <button type="button" onClick={() => removeGeneralExamNote(note.id)} className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-pink-500" aria-label="Remove examination note" data-id="element-8217" data-mp="2twm92jbwysfe">
                                <Trash2 className="h-4 w-4" data-id="element-8218" data-mp="2b0codd6ib3mf" />
                              </button>
                            </li>)}
                      </ul>}
                    <label htmlFor="general-exam-note" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8219" data-mp="47jyew2d2p7z">
                      New note
                    </label>
                    <textarea id="general-exam-note" rows={3} className="w-full p-2 border rounded text-sm" value={generalExamNote} onChange={e => setGeneralExamNote(e.target.value)} placeholder="e.g. Patient comfortable, no oedema noted on review at 14:20." data-id="element-8220" data-mp="uk7323f7icji" />
                    <Button variant="outline" className="mt-3" onClick={addGeneralExamNote} disabled={!generalExamNote.trim()} data-id="element-8221" data-mp="1dhhqmqrh7hj4">
                      + Add Note
                    </Button>
                  </Card>

                  <Card title="Present Pregnancy" data-id="element-8222" data-mp="1s8i9lppumtti">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-8223" data-mp="1x6s9ueqfkklz">
                      <Input type="date" label="LMP" value={selectedMaternityFile.presentPregnancy.lmp} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        lmp: e.target.value
                      }
                    })} data-id="element-8224" data-mp="216kj6tnneuse" />
                      <Input label="Quickening" value={selectedMaternityFile.presentPregnancy.quickening} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        quickening: e.target.value
                      }
                    })} data-id="element-8225" data-mp="48mzd4q43o7y" />
                      <Input type="date" label="EDD" value={selectedMaternityFile.presentPregnancy.edd} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        edd: e.target.value
                      }
                    })} data-id="element-8226" data-mp="2ljaqtdxdud64" />
                      <Input label="Gestation Age (Weeks)" value={selectedMaternityFile.presentPregnancy.gestationAge} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        gestationAge: e.target.value
                      }
                    })} data-id="element-8227" data-mp="1trv83uyy5al6" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-8228" data-mp="kr8wte0zlmgs">
                      <Input label="Summary of ANC Findings 1st BP" value={selectedMaternityFile.presentPregnancy.ancSummaryFirstBp} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        ancSummaryFirstBp: e.target.value
                      }
                    })} data-id="element-8229" data-mp="ft7dcrznfl4s" />
                      <Input label="At Weeks" value={selectedMaternityFile.presentPregnancy.ancSummaryAtWeeks} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        ancSummaryAtWeeks: e.target.value
                      }
                    })} data-id="element-8230" data-mp="3put1ssx0obw8" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-8231" data-mp="qdbb4dvq9u6k">
                      <Input type="date" label="Syphilis Test Date" value={selectedMaternityFile.presentPregnancy.syphilisTest.date} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        syphilisTest: {
                          ...selectedMaternityFile.presentPregnancy.syphilisTest,
                          date: e.target.value
                        }
                      }
                    })} data-id="element-8232" data-mp="10hrqz797jbad" />
                      <Input label="Result" value={selectedMaternityFile.presentPregnancy.syphilisTest.result} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        syphilisTest: {
                          ...selectedMaternityFile.presentPregnancy.syphilisTest,
                          result: e.target.value
                        }
                      }
                    })} data-id="element-8233" data-mp="1y7mpviis5u2i" />
                      <Input label="Treatment" value={selectedMaternityFile.presentPregnancy.syphilisTest.treatment} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        syphilisTest: {
                          ...selectedMaternityFile.presentPregnancy.syphilisTest,
                          treatment: e.target.value
                        }
                      }
                    })} data-id="element-8234" data-mp="3pwg8ic3utytf" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-8235" data-mp="1lmlbo6h6n0xy">
                      <Input type="date" label="HP Test Date" value={selectedMaternityFile.presentPregnancy.hpTest.date} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        hpTest: {
                          ...selectedMaternityFile.presentPregnancy.hpTest,
                          date: e.target.value
                        }
                      }
                    })} data-id="element-8236" data-mp="29cyy9hh007l5" />
                      <Input label="Result" value={selectedMaternityFile.presentPregnancy.hpTest.result} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        hpTest: {
                          ...selectedMaternityFile.presentPregnancy.hpTest,
                          result: e.target.value
                        }
                      }
                    })} data-id="element-8237" data-mp="1qd7cjcfsjxm5" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8238" data-mp="3eapxt0bcrsl5">
                      <Input label="Pelvic Assessment" value={selectedMaternityFile.presentPregnancy.pelvicAssessment} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        pelvicAssessment: e.target.value
                      }
                    })} data-id="element-8239" data-mp="3j8rhnnglrvxo" />
                      <Input label="Abnormalities" value={selectedMaternityFile.presentPregnancy.abnormalities} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        abnormalities: e.target.value
                      }
                    })} data-id="element-8240" data-mp="161s40eok4yt7" />
                    </div>
                  </Card>

                  <Card title="Observations for Latent Phase Labour" data-id="element-8241" data-mp="263yb8983gn73">
                    <div className="overflow-x-auto" data-id="element-8242" data-mp="2b29lum67r1qd">
                      <table className="w-full text-sm text-left" data-id="element-8243" data-mp="2b0yksu45k82y">
                        <thead className="bg-gray-50" data-id="element-8244" data-mp="1x9a9gt1q9nn1">
                          <tr data-id="element-8245" data-mp="21bx1ov2b9akr">
                            <th className="p-2 border" data-id="element-8246" data-mp="21b563k8prcuh">Date</th>
                            <th className="p-2 border" data-id="element-8247" data-mp="1nc5h2bhagvxs">Time</th>
                            <th className="p-2 border" data-id="element-8248" data-mp="1nakpmuq9tt45">Pulse</th>
                            <th className="p-2 border" data-id="element-8249" data-mp="1sbiflthivrg7">BP</th>
                            <th className="p-2 border" data-id="element-8250" data-mp="1s8vz34cnm1lu">FH</th>
                            <th className="p-2 border" data-id="element-8251" data-mp="1ecsfe4zepbnm">Temp</th>
                            <th className="p-2 border" data-id="element-8252" data-mp="1icj668oxnrs5">Urinalysis</th>
                            <th className="p-2 border" data-id="element-8253" data-mp="1ievv55t3z2ep">Contractions</th>
                            <th className="p-2 border" data-id="element-8254" data-mp="38q091tbnbh7o">Cervical Dilation</th>
                            <th className="p-2 border" data-id="element-8255" data-mp="3cq2ac1dgy8mf">Membranes Liquor</th>
                            <th className="p-2 border" data-id="element-8256" data-mp="pj5v9988fe52">Remarks</th>
                            <th className="p-2 border" data-id="element-8257" data-mp="pg9wxjsh2ng3">Sign</th>
                            <th className="p-2 border" data-id="element-8258" data-mp="kirzoqnthwnx"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-8259" data-mp="gi7xv9sau4vg">
                          {selectedMaternityFile.labourObservations.map((obs, index) => <tr key={obs.id} data-id="element-8260" data-mp="189dv9igmxoh1" data-mpi={index}>
                                <td className="p-1 border" data-id="element-8261" data-mp="18b8duq4fp4hy">
                                  <input className="w-full p-1 border rounded" value={obs.date} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].date = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-8262" data-mp="148lkgks47bin" />
                                </td>
                                <td className="p-1 border" data-id="element-8263" data-mp="28inbvh747cc4">
                                  <input className="w-full p-1 border rounded" value={obs.time} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].time = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-8264" data-mp="2dh6vhay8kc1b" />
                                </td>
                                <td className="p-1 border" data-id="element-8265" data-mp="9820ltclqyq">
                                  <input className="w-full p-1 border rounded" value={obs.pulse} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].pulse = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-8266" data-mp="3rfhqcndclrw3" />
                                </td>
                                <td className="p-1 border" data-id="element-8267" data-mp="10b4q9cq9c8gf">
                                  <input className="w-full p-1 border rounded" value={obs.bp} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].bp = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-8268" data-mp="ll5ufgm6d944" />
                                </td>
                                <td className="p-1 border" data-id="element-8269" data-mp="17dvgf3twckab">
                                  <input className="w-full p-1 border rounded" value={obs.fh} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].fh = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-8270" data-mp="1bgjp9bim1r7a" />
                                </td>
                                <td className="p-1 border" data-id="element-8271" data-mp="2dva87klfeipi">
                                  <input className="w-full p-1 border rounded" value={obs.temp} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].temp = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-8272" data-mp="2dwtt5oi11hxm" />
                                </td>
                                <td className="p-1 border" data-id="element-8273" data-mp="13whiv90y6hda">
                                  <input className="w-full p-1 border rounded" value={obs.urinalysis} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].urinalysis = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-8274" data-mp="13z3qf8nu8wvi" />
                                </td>
                                <td className="p-1 border" data-id="element-8275" data-mp="3nrch7d25yc77">
                                  <input className="w-full p-1 border rounded" value={obs.contractions} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].contractions = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-8276" data-mp="3nsnnpkmzty20" />
                                </td>
                                <td className="p-1 border" data-id="element-8277" data-mp="1v2latvvwt7f7">
                                  <input className="w-full p-1 border rounded" value={obs.cervicalDilation} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].cervicalDilation = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-8278" data-mp="1q40ck207wxte" />
                                </td>
                                <td className="p-1 border" data-id="element-8279" data-mp="2ogweffv4fux7">
                                  <input className="w-full p-1 border rounded" value={obs.membranesLiquor} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].membranesLiquor = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-8280" data-mp="2oih8l4dixbuj" />
                                </td>
                                <td className="p-1 border" data-id="element-8281" data-mp="1flaf3y0mhav9">
                                  <input className="w-full p-1 border rounded" value={obs.remarks} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].remarks = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-8282" data-mp="1thxoj09rhti2" />
                                </td>
                                <td className="p-1 border" data-id="element-8283" data-mp="2d4vxjkvsjcqc">
                                  <input className="w-full p-1 border rounded" value={obs.sign} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].sign = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-8284" data-mp="2i504aj6wct3d" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-8285" data-mp="bb98bbzjzv1o">
                                  <button onClick={() => {
                              const newObs = selectedMaternityFile.labourObservations.filter((_, i) => i !== index);
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} className="text-red-500 hover:text-red-700" data-id="element-8286" data-mp="18w5aape5eloi">
                                    <Trash2 className="w-4 h-4" data-id="element-8287" data-mp="1026fmtnzu6fr" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    labourObservations: [...selectedMaternityFile.labourObservations, {
                      id: Date.now().toString(),
                      date: '',
                      time: '',
                      pulse: '',
                      bp: '',
                      fh: '',
                      temp: '',
                      urinalysis: '',
                      contractions: '',
                      cervicalDilation: '',
                      membranesLiquor: '',
                      remarks: '',
                      sign: ''
                    }]
                  })} data-id="element-8288" data-mp="1iyz5sh1v6r5v">
                      + Add Observation
                    </Button>
                  </Card>

                  <Card title="Description of Labour and Delivery" data-id="element-8289" data-mp="38g36cd5rosd0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-8290" data-mp="38es2yeg8hmmf">
                      <Input label="Laceration" value={selectedMaternityFile.delivery.laceration} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        laceration: e.target.value
                      }
                    })} data-id="element-8291" data-mp="2kp7fbi94y16t" />
                      <Input label="Anterior" value={selectedMaternityFile.delivery.anterior} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        anterior: e.target.value
                      }
                    })} data-id="element-8292" data-mp="1wqso6kuognha" />
                      <Input label="Posterior" value={selectedMaternityFile.delivery.posterior} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        posterior: e.target.value
                      }
                    })} data-id="element-8293" data-mp="7dfe0ezvw589" />
                      <Input label="None" value={selectedMaternityFile.delivery.none} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        none: e.target.value
                      }
                    })} data-id="element-8294" data-mp="36mxs2yvir3fq" />
                      <Input label="Repaired" value={selectedMaternityFile.delivery.repaired} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        repaired: e.target.value
                      }
                    })} data-id="element-8295" data-mp="2jmqljzfoc6d8" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-8296" data-mp="3geu5sxzyt86n">
                      <Input label="Membranes" value={selectedMaternityFile.delivery.membranes} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        membranes: e.target.value
                      }
                    })} data-id="element-8297" data-mp="3cc7jtdpz6hhd" />
                      <Input type="date" label="Date" value={selectedMaternityFile.delivery.membranesDate} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        membranesDate: e.target.value
                      }
                    })} data-id="element-8298" data-mp="3slz1apsgh6n" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.membranesTime} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        membranesTime: e.target.value
                      }
                    })} data-id="element-8299" data-mp="3792jce7cz98g" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-8300" data-mp="2cg1p9b7m9ahf">
                      <Input label="Episiotomy" value={selectedMaternityFile.delivery.episiotomy} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        episiotomy: e.target.value
                      }
                    })} data-id="element-8301" data-mp="27j1lj6y8ojth" />
                      <Input label="Repaired" value={selectedMaternityFile.delivery.episiotomyRepaired} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        episiotomyRepaired: e.target.value
                      }
                    })} data-id="element-8302" data-mp="i0mjxis9zoyx" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-8303" data-mp="1arwh1q6y7g0i">
                      <Input label="Maternal ARV" value={selectedMaternityFile.delivery.maternalARV} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        maternalARV: e.target.value
                      }
                    })} data-id="element-8304" data-mp="1fnbsycleb1ua" />
                      <Input label="Which ones (NPV, AZT, Other)" value={selectedMaternityFile.delivery.arvTypes} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        arvTypes: e.target.value
                      }
                    })} data-id="element-8305" data-mp="2by9b0hkeig2" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-8306" data-mp="1zzskq23sttg1">
                      <Input label="Uterine Massage" value={selectedMaternityFile.delivery.uterineMassage} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        uterineMassage: e.target.value
                      }
                    })} data-id="element-8307" data-mp="1lacvwrntvy2z" />
                      <Input label="Mother Already on HAART" value={selectedMaternityFile.delivery.motherOnHAART} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        motherOnHAART: e.target.value
                      }
                    })} data-id="element-8308" data-mp="tioqyq7dqdu6" />
                      <Input label="Cytotec" value={selectedMaternityFile.delivery.cytotec} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        cytotec: e.target.value
                      }
                    })} data-id="element-8309" data-mp="1baeycobymha2" />
                      <Input label="Syntometrine" value={selectedMaternityFile.delivery.syntometrine} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        syntometrine: e.target.value
                      }
                    })} data-id="element-8310" data-mp="2mutufqmj6iq7" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-8311" data-mp="3i3e7b93rupx3">
                      <Input type="date" label="1st Stage Began Date" value={selectedMaternityFile.delivery.firstStageBeganDate} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        firstStageBeganDate: e.target.value
                      }
                    })} data-id="element-8312" data-mp="3mv5owu4y9lb3" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.firstStageBeganTime} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        firstStageBeganTime: e.target.value
                      }
                    })} data-id="element-8313" data-mp="1bh3t76dsguk" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-8314" data-mp="11c2r0618c4ou">
                      <Input label="Placenta Delivery" value={selectedMaternityFile.delivery.placentaDelivery} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        placentaDelivery: e.target.value
                      }
                    })} data-id="element-8315" data-mp="11e6uimv7v1ol" />
                      <Input type="date" label="Delivered Date" value={selectedMaternityFile.delivery.placentaDeliveredDate} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        placentaDeliveredDate: e.target.value
                      }
                    })} data-id="element-8316" data-mp="8p9njfwggrb5" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.placentaDeliveredTime} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        placentaDeliveredTime: e.target.value
                      }
                    })} data-id="element-8317" data-mp="1rneassvsanfp" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-8318" data-mp="3bjldgv6w2xg0">
                      <Input type="date" label="Complete Dilation Date" value={selectedMaternityFile.delivery.completeDilationDate} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        completeDilationDate: e.target.value
                      }
                    })} data-id="element-8319" data-mp="3fkndvfcjlfjk" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.completeDilationTime} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        completeDilationTime: e.target.value
                      }
                    })} data-id="element-8320" data-mp="h2rmdq7wa4w6" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-8321" data-mp="uv11t25h04bu">
                      <Input label="Abnormality at Dilation" value={selectedMaternityFile.delivery.abnormalityAtDilation} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        abnormalityAtDilation: e.target.value
                      }
                    })} data-id="element-8322" data-mp="18u0nq21lm54b" />
                      <Input label="Describe" value={selectedMaternityFile.delivery.abnormalityAtDilationDesc} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        abnormalityAtDilationDesc: e.target.value
                      }
                    })} data-id="element-8323" data-mp="2duqgvwzqb5ak" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-8324" data-mp="3u907yyvcv853">
                      <Input label="Postpartum Hemorrhage" value={selectedMaternityFile.delivery.postpartumHemorrhage} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        postpartumHemorrhage: e.target.value
                      }
                    })} data-id="element-8325" data-mp="7wqcqiowywpn" />
                      <Input label="Delivery Method" value={selectedMaternityFile.delivery.deliveryMethod} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        deliveryMethod: e.target.value
                      }
                    })} data-id="element-8326" data-mp="37d54q3mn27ou" />
                      <Input label="EBL (cc)" value={selectedMaternityFile.delivery.ebl} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        ebl: e.target.value
                      }
                    })} data-id="element-8327" data-mp="1mszyor6bafgf" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-8328" data-mp="bffprako7vtm">
                      <Input label="Abnormality at Delivery" value={selectedMaternityFile.delivery.abnormalityAtDelivery} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        abnormalityAtDelivery: e.target.value
                      }
                    })} data-id="element-8329" data-mp="fghf8zosctie" />
                      <Input label="Describe" value={selectedMaternityFile.delivery.abnormalityAtDeliveryDesc} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        abnormalityAtDeliveryDesc: e.target.value
                      }
                    })} data-id="element-8330" data-mp="3ixj8psgd5qqt" />
                      <Input label="Complications" value={selectedMaternityFile.delivery.complications} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        complications: e.target.value
                      }
                    })} data-id="element-8331" data-mp="g5b9sesusj38" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-8332" data-mp="3jn34i0ydjh54">
                      Delivery Summary
                    </h4>
                    <div className="overflow-x-auto" data-id="element-8333" data-mp="3ly0hw79l4tn1">
                      <table className="w-full text-sm text-left" data-id="element-8334" data-mp="3lzjxdvnf30nr">
                        <thead className="bg-gray-50" data-id="element-8335" data-mp="3qxd1mj55tkle">
                          <tr data-id="element-8336" data-mp="3uyf1912znynw">
                            <th className="p-2 border" data-id="element-8337" data-mp="3ux3woz1pymvq">Date</th>
                            <th className="p-2 border" data-id="element-8338" data-mp="3ruc5mwhv0ti">Time of Birth</th>
                            <th className="p-2 border" data-id="element-8339" data-mp="3precgp3o3e8">Sex</th>
                            <th className="p-2 border" data-id="element-8340" data-mp="8pw0oncovscd">Weight</th>
                            <th className="p-2 border" data-id="element-8341" data-mp="dps4alhh5hre">Delivered By</th>
                            <th className="p-2 border" data-id="element-8342" data-mp="dnxf2uz673hi"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-8343" data-mp="mic25jpxzib6">
                          {selectedMaternityFile.delivery.deliverySummary.map((sum, index) => <tr key={sum.id} data-id="element-8344" data-mp="rj9n242c6qgd" data-mpi={index}>
                                <td className="p-1 border" data-id="element-8345" data-mp="wg00ejmrb7y3">
                                  <Input type="date" value={sum.date} onChange={e => {
                              const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                              newSum[index].date = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                delivery: {
                                  ...selectedMaternityFile.delivery,
                                  deliverySummary: newSum
                                }
                              });
                            }} data-id="element-8346" data-mp="wef7szk05z2v" />
                                </td>
                                <td className="p-1 border" data-id="element-8347" data-mp="1d6rqjhoztn7">
                                  <Input type="time" value={sum.timeOfBirth} onChange={e => {
                              const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                              newSum[index].timeOfBirth = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                delivery: {
                                  ...selectedMaternityFile.delivery,
                                  deliverySummary: newSum
                                }
                              });
                            }} data-id="element-8348" data-mp="1dow63eide67" />
                                </td>
                                <td className="p-1 border" data-id="element-8349" data-mp="2nkl69zz6qega">
                                  <Input value={sum.sex} onChange={e => {
                              const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                              newSum[index].sex = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                delivery: {
                                  ...selectedMaternityFile.delivery,
                                  deliverySummary: newSum
                                }
                              });
                            }} data-id="element-8350" data-mp="2nm5vrb6n777a" />
                                </td>
                                <td className="p-1 border" data-id="element-8351" data-mp="1sag69cxylqlk">
                                  <Input value={sum.weight} onChange={e => {
                              const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                              newSum[index].weight = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                delivery: {
                                  ...selectedMaternityFile.delivery,
                                  deliverySummary: newSum
                                }
                              });
                            }} data-id="element-8352" data-mp="1xakop8ifgvef" />
                                </td>
                                <td className="p-1 border" data-id="element-8353" data-mp="3012ahbnixi30">
                                  <Input value={sum.deliveredBy} onChange={e => {
                              const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                              newSum[index].deliveredBy = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                delivery: {
                                  ...selectedMaternityFile.delivery,
                                  deliverySummary: newSum
                                }
                              });
                            }} data-id="element-8354" data-mp="2v30uizle9yvr" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-8355" data-mp="1essyvcsemy26">
                                  <button onClick={() => {
                              const newSum = selectedMaternityFile.delivery.deliverySummary.filter((_, i) => i !== index);
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                delivery: {
                                  ...selectedMaternityFile.delivery,
                                  deliverySummary: newSum
                                }
                              });
                            }} className="text-red-500 hover:text-red-700" data-id="element-8356" data-mp="1itmocalhwkx6">
                                    <Trash2 className="w-4 h-4" data-id="element-8357" data-mp="sk5c80d7bxsg" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    delivery: {
                      ...selectedMaternityFile.delivery,
                      deliverySummary: [...selectedMaternityFile.delivery.deliverySummary, {
                        id: Date.now().toString(),
                        date: '',
                        timeOfBirth: '',
                        sex: '',
                        weight: '',
                        deliveredBy: ''
                      }]
                    }
                  })} data-id="element-8358" data-mp="3r2aydqdua3rv">
                      + Add Baby
                    </Button>
                  </Card>

                  <Card title="Newborn Evaluation" data-id="element-8359" data-mp="1dyh64nzfeyp">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-8360" data-mp="6c8eolx6irnh">
                      <Input label="EGA" value={selectedMaternityFile.newbornEvaluation.ega} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        ega: e.target.value
                      }
                    })} data-id="element-8361" data-mp="batb0a9b8r6n" />
                      <Input label="Alive" value={selectedMaternityFile.newbornEvaluation.alive} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        alive: e.target.value
                      }
                    })} data-id="element-8362" data-mp="1qt96e86bu7ri" />
                      <Input label="Neonatal Death" value={selectedMaternityFile.newbornEvaluation.neonatalDeath} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        neonatalDeath: e.target.value
                      }
                    })} data-id="element-8363" data-mp="131k6o6r6eh0j" />
                      <Input label="Cause of Death" value={selectedMaternityFile.newbornEvaluation.causeOfDeath} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        causeOfDeath: e.target.value
                      }
                    })} data-id="element-8364" data-mp="bwkqzym4dk9y" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-8365" data-mp="1c5apogmy8jr0">
                      <Input label="Apgar 1 Min" value={selectedMaternityFile.newbornEvaluation.apgar1} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        apgar1: e.target.value
                      }
                    })} data-id="element-8366" data-mp="1gvr5g7e8shgj" />
                      <Input label="Apgar 5 Min" value={selectedMaternityFile.newbornEvaluation.apgar5} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        apgar5: e.target.value
                      }
                    })} data-id="element-8367" data-mp="jvn2h67xpc1" />
                      <Input label="Apgar 10 Min" value={selectedMaternityFile.newbornEvaluation.apgar10} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        apgar10: e.target.value
                      }
                    })} data-id="element-8368" data-mp="1roj4cn8wrb00" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-8369" data-mp="2p1pwktnzsjrr">
                      <Input label="General Exam" value={selectedMaternityFile.newbornEvaluation.generalExam} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        generalExam: e.target.value
                      }
                    })} data-id="element-8370" data-mp="2u1vped600cbf" />
                      <Input label="Congenital Malformation" value={selectedMaternityFile.newbornEvaluation.congenitalMalformation} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        congenitalMalformation: e.target.value
                      }
                    })} data-id="element-8371" data-mp="1ndovzosfublk" />
                      <Input label="Describe" value={selectedMaternityFile.newbornEvaluation.congenitalMalformationDesc} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        congenitalMalformationDesc: e.target.value
                      }
                    })} data-id="element-8372" data-mp="wlzrwsfhf039" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-8373" data-mp="2ycyux1k9csd3">
                      <Input label="Infant ARVs" value={selectedMaternityFile.newbornEvaluation.infantARVs} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        infantARVs: e.target.value
                      }
                    })} data-id="element-8374" data-mp="339yrmlv7wirj" />
                      <Input label="Infant Feeding" value={selectedMaternityFile.newbornEvaluation.infantFeeding} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        infantFeeding: e.target.value
                      }
                    })} data-id="element-8375" data-mp="36btijrn8aniq" />
                      <Input label="Resuscitation Methods" value={selectedMaternityFile.newbornEvaluation.resuscitationMethods} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        resuscitationMethods: e.target.value
                      }
                    })} data-id="element-8376" data-mp="2xcokemfkww73" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-8377" data-mp="ac9gpeqfi9gs">
                      <Input label="Crown Heel Length" value={selectedMaternityFile.newbornEvaluation.crownHeelLength} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        crownHeelLength: e.target.value
                      }
                    })} data-id="element-8378" data-mp="y4zpjf31ccmu" />
                      <Input label="Head Circumference" value={selectedMaternityFile.newbornEvaluation.headCircumference} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        headCircumference: e.target.value
                      }
                    })} data-id="element-8379" data-mp="4cvgaez4vwa0" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-8380" data-mp="1bttd5j6cadx7">
                      Physical Examination
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-8381" data-mp="1edg8x1v3uucx">
                      <Input label="Skin Jaundice" value={selectedMaternityFile.newbornEvaluation.skinJaundice} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        skinJaundice: e.target.value
                      }
                    })} data-id="element-8382" data-mp="1japuu7jacnw0" />
                      <Input label="Skin Other" value={selectedMaternityFile.newbornEvaluation.skinOther} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        skinOther: e.target.value
                      }
                    })} data-id="element-8383" data-mp="2c29ic3mx0o3t" />
                      <Input label="Fontanelles" value={selectedMaternityFile.newbornEvaluation.fontanelles} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        fontanelles: e.target.value
                      }
                    })} data-id="element-8384" data-mp="aaksfmlr20aj" />
                      <Input label="Eyes" value={selectedMaternityFile.newbornEvaluation.eyes} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        eyes: e.target.value
                      }
                    })} data-id="element-8385" data-mp="2ip99o2i9jtr7" />
                      <Input label="Eyes Treatment" value={selectedMaternityFile.newbornEvaluation.eyesTreatment} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        eyesTreatment: e.target.value
                      }
                    })} data-id="element-8386" data-mp="28tphnr6tlf7q" />
                      <Input label="Mouth" value={selectedMaternityFile.newbornEvaluation.mouth} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        mouth: e.target.value
                      }
                    })} data-id="element-8387" data-mp="2qv3e786l73et" />
                      <Input label="Neck" value={selectedMaternityFile.newbornEvaluation.neck} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        neck: e.target.value
                      }
                    })} data-id="element-8388" data-mp="1af94nf1x782s" />
                      <Input label="Chest" value={selectedMaternityFile.newbornEvaluation.chest} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        chest: e.target.value
                      }
                    })} data-id="element-8389" data-mp="oz48yro5jlfq" />
                      <Input label="Abdomen" value={selectedMaternityFile.newbornEvaluation.abdomen} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        abdomen: e.target.value
                      }
                    })} data-id="element-8390" data-mp="21brlpxe9zitb" />
                      <Input label="Umbilicus" value={selectedMaternityFile.newbornEvaluation.umbilicus} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        umbilicus: e.target.value
                      }
                    })} data-id="element-8391" data-mp="18mkqp8e15bct" />
                      <Input label="Genitalia" value={selectedMaternityFile.newbornEvaluation.genitalia} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        genitalia: e.target.value
                      }
                    })} data-id="element-8392" data-mp="3qx9urtc0k45d" />
                      <Input label="Urine" value={selectedMaternityFile.newbornEvaluation.urine} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        urine: e.target.value
                      }
                    })} data-id="element-8393" data-mp="2bewvgkau7g2u" />
                      <Input label="Anus" value={selectedMaternityFile.newbornEvaluation.anus} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        anus: e.target.value
                      }
                    })} data-id="element-8394" data-mp="12bq074kw04cb" />
                      <Input label="Meconium" value={selectedMaternityFile.newbornEvaluation.meconium} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        meconium: e.target.value
                      }
                    })} data-id="element-8395" data-mp="35i5i1q86mp5y" />
                      <Input label="Upper Limbs" value={selectedMaternityFile.newbornEvaluation.upperLimbs} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        upperLimbs: e.target.value
                      }
                    })} data-id="element-8396" data-mp="3fglapxmuy6hk" />
                      <Input label="Erb Paralysis" value={selectedMaternityFile.newbornEvaluation.erbParalysis} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        erbParalysis: e.target.value
                      }
                    })} data-id="element-8397" data-mp="36l2wa1bu8y2a" />
                      <Input label="Lower Limbs" value={selectedMaternityFile.newbornEvaluation.lowerLimbs} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        lowerLimbs: e.target.value
                      }
                    })} data-id="element-8398" data-mp="2evhor4yvg988" />
                      <Input label="Ortolani Sign" value={selectedMaternityFile.newbornEvaluation.ortolaniSign} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        ortolaniSign: e.target.value
                      }
                    })} data-id="element-8399" data-mp="1xczpatymfg23" />
                      <Input label="Back" value={selectedMaternityFile.newbornEvaluation.back} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        back: e.target.value
                      }
                    })} data-id="element-8400" data-mp="a6083uop3v1q" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-8401" data-mp="2uv7kgtc586e2">
                      Reflexes
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-8402" data-mp="15fda708uq930">
                      <Input label="Grasp" value={selectedMaternityFile.newbornEvaluation.reflexesGrasp} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        reflexesGrasp: e.target.value
                      }
                    })} data-id="element-8403" data-mp="1a6litu2cxqf4" />
                      <Input label="Moro (Startle)" value={selectedMaternityFile.newbornEvaluation.reflexesMoro} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        reflexesMoro: e.target.value
                      }
                    })} data-id="element-8404" data-mp="1k3hvmbzagyph" />
                      <Input label="Sucking" value={selectedMaternityFile.newbornEvaluation.reflexesSucking} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        reflexesSucking: e.target.value
                      }
                    })} data-id="element-8405" data-mp="322ei2slq06on" />
                    </div>
                    <div className="mt-4" data-id="element-8406" data-mp="ytmah7mfe77e">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8407" data-mp="yskucoqjyuit">
                        Newborn Progress Notes
                      </label>
                      <textarea className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-pink-500 focus:outline-none" rows={4} value={selectedMaternityFile.newbornEvaluation.progressNotes} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        progressNotes: e.target.value
                      }
                    })} data-id="element-8408" data-mp="18k72udbiafhv" />
                    </div>
                  </Card>

                  <Card title="Post Delivery Observation Chart" data-id="element-8409" data-mp="1tvwkpmvon4f8">
                    <div className="overflow-x-auto" data-id="element-8410" data-mp="28sexvdncfygz">
                      <table className="w-full text-sm text-left" data-id="element-8411" data-mp="28t6veu0i469w">
                        <thead className="bg-gray-50" data-id="element-8412" data-mp="23rr7mu6xvlnt">
                          <tr data-id="element-8413" data-mp="2hsk6ts86r4x9">
                            <th className="p-2 border" colSpan={2} data-id="element-8414" data-mp="2hrs848jakr2r">
                              Time
                            </th>
                            <th className="p-2 border text-center" colSpan={8} data-id="element-8415" data-mp="2ct78obbv6bfd">
                              Mother
                            </th>
                            <th className="p-2 border text-center" colSpan={7} data-id="element-8416" data-mp="2miqspkbbcbbt">
                              Baby
                            </th>
                            <th className="p-2 border" rowSpan={2} data-id="element-8417" data-mp="30jbbwhl5495u">
                              Sign
                            </th>
                            <th className="p-2 border" rowSpan={2} data-id="element-8418" data-mp="3f5qqvbd497dx"></th>
                          </tr>
                          <tr data-id="element-8419" data-mp="3k4laugmtl1zm">
                            <th className="p-2 border" data-id="element-8420" data-mp="3k3sd255u9mtm">Date</th>
                            <th className="p-2 border" data-id="element-8421" data-mp="365c6ce7z7wbh">Time</th>
                            <th className="p-2 border" data-id="element-8422" data-mp="364soexibt7xg">BP</th>
                            <th className="p-2 border" data-id="element-8423" data-mp="3b3dlio2a7bho">Pulse</th>
                            <th className="p-2 border" data-id="element-8424" data-mp="2wf955qqpbc22">Resps</th>
                            <th className="p-2 border" data-id="element-8425" data-mp="2wdy07nliry9d">Temp</th>
                            <th className="p-2 border" data-id="element-8426" data-mp="3168ykm0m3bzt">PV Bleed</th>
                            <th className="p-2 border" data-id="element-8427" data-mp="2nekqctthh7o4">Uterus</th>
                            <th className="p-2 border" data-id="element-8428" data-mp="2ngfaw70uexro">Bladder</th>
                            <th className="p-2 border" data-id="element-8429" data-mp="2rfe9zazw80b7">Pallor</th>
                            <th className="p-2 border" data-id="element-8430" data-mp="2dgelq4galnfk">Temp</th>
                            <th className="p-2 border" data-id="element-8431" data-mp="2dh6kfo56s1a2">Resps</th>
                            <th className="p-2 border" data-id="element-8432" data-mp="2ifrawvumz6lb">Cord</th>
                            <th className="p-2 border" data-id="element-8433" data-mp="nz9bynql94j7">Skin</th>
                            <th className="p-2 border" data-id="element-8434" data-mp="o0u6idcwknh2">Feed BF/AF</th>
                            <th className="p-2 border" data-id="element-8435" data-mp="j8axltp5ugil">Bowel</th>
                            <th className="p-2 border" data-id="element-8436" data-mp="f6z2x9rfj53j">Urine</th>
                          </tr>
                        </thead>
                        <tbody data-id="element-8437" data-mp="a8nr4thkqkio">
                          {selectedMaternityFile.postDeliveryObservations.map((obs, index) => <tr key={obs.id} data-id="element-8438" data-mp="16vspdp8fdelk" data-mpi={index}>
                                <td className="p-1 border" data-id="element-8439" data-mp="12xlbd25cire2">
                                  <input className="w-full p-1 border rounded" value={obs.date} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].date = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8440" data-mp="xxgqkzxenai1" />
                                </td>
                                <td className="p-1 border" data-id="element-8441" data-mp="3ma4ch1hnq0j9">
                                  <input className="w-full p-1 border rounded" value={obs.time} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].time = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8442" data-mp="3ra8uwx24l5c4" />
                                </td>
                                <td className="p-1 border" data-id="element-8443" data-mp="hel16xhxcv0v">
                                  <input className="w-full p-1 border rounded" value={obs.bp} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].bp = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8444" data-mp="3pstmdki4ajj0" />
                                </td>
                                <td className="p-1 border" data-id="element-8445" data-mp="17oby8gweoz4f">
                                  <input className="w-full p-1 border rounded" value={obs.pulse} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].pulse = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8446" data-mp="1cm3s3ggm2vb9" />
                                </td>
                                <td className="p-1 border" data-id="element-8447" data-mp="3uzbda8gb0rqm">
                                  <input className="w-full p-1 border rounded" value={obs.resps} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].resps = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8448" data-mp="3m8foj44eevh" />
                                </td>
                                <td className="p-1 border" data-id="element-8449" data-mp="2lqgbhshbdg17">
                                  <input className="w-full p-1 border rounded" value={obs.temp} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].temp = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8450" data-mp="2gs4yj8vq2per" />
                                </td>
                                <td className="p-1 border" data-id="element-8451" data-mp="3iiju6mlyi9si">
                                  <input className="w-full p-1 border rounded" value={obs.pvBleeding} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].pvBleeding = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8452" data-mp="1gsb6jjqk6xa8" />
                                </td>
                                <td className="p-1 border" data-id="element-8453" data-mp="e4oiunbn18i4">
                                  <input className="w-full p-1 border rounded" value={obs.uterus} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].uterus = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8454" data-mp="j5ch9u274rmd" />
                                </td>
                                <td className="p-1 border" data-id="element-8455" data-mp="2iluszzjee0tu">
                                  <input className="w-full p-1 border rounded" value={obs.bladder} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].bladder = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8456" data-mp="1uxblhm869s2t" />
                                </td>
                                <td className="p-1 border" data-id="element-8457" data-mp="2w8v7b8nzpnm6">
                                  <input className="w-full p-1 border rounded" value={obs.pallor} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].pallor = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8458" data-mp="318g87omq061j" />
                                </td>
                                <td className="p-1 border" data-id="element-8459" data-mp="18cx3c8pqff33">
                                  <input className="w-full p-1 border rounded" value={obs.babyTemp} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].babyTemp = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8460" data-mp="18bc7mfrolq3n" />
                                </td>
                                <td className="p-1 border" data-id="element-8461" data-mp="2173bniji55vn">
                                  <input className="w-full p-1 border rounded" value={obs.babyResps} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].babyResps = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8462" data-mp="218mrx955rgxf" />
                                </td>
                                <td className="p-1 border" data-id="element-8463" data-mp="1hteg0dnygent">
                                  <input className="w-full p-1 border rounded" value={obs.cord} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].cord = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8464" data-mp="1hrtlup5jyxqh" />
                                </td>
                                <td className="p-1 border" data-id="element-8465" data-mp="w4aeqw9rufny">
                                  <input className="w-full p-1 border rounded" value={obs.skinColour} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].skinColour = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8466" data-mp="w1o5ms78fs3m" />
                                </td>
                                <td className="p-1 border" data-id="element-8467" data-mp="1ox47cdwwu2wh">
                                  <input className="w-full p-1 border rounded" value={obs.feedingBF} placeholder="BF" onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].feedingBF = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8468" data-mp="1jz2mpoju5viw" />
                                  <input className="w-full p-1 border rounded mt-1" value={obs.feedingAF} placeholder="AF" onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].feedingAF = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8469" data-mp="l3s51zxu9w0f" />
                                </td>
                                <td className="p-1 border" data-id="element-8470" data-mp="1v330dzus7pxr">
                                  <input className="w-full p-1 border rounded" value={obs.eliminationBowel} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].eliminationBowel = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8471" data-mp="1zz0h479yiyb2" />
                                </td>
                                <td className="p-1 border" data-id="element-8472" data-mp="20vgud6j57pnf">
                                  <input className="w-full p-1 border rounded" value={obs.eliminationUrine} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].eliminationUrine = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8473" data-mp="1vxnmm926ynl1" />
                                </td>
                                <td className="p-1 border" data-id="element-8474" data-mp="35vnbg1eb0vnk">
                                  <input className="w-full p-1 border rounded" value={obs.sign} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].sign = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-8475" data-mp="35u3wqf8aqsnw" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-8476" data-mp="g5tkeajq71ag">
                                  <button onClick={() => {
                              const newObs = selectedMaternityFile.postDeliveryObservations.filter((_, i) => i !== index);
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} className="text-red-500 hover:text-red-700" data-id="element-8477" data-mp="g7wi7gr4dypq">
                                    <Trash2 className="w-4 h-4" data-id="element-8478" data-mp="2snpxe166cwfb" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    postDeliveryObservations: [...selectedMaternityFile.postDeliveryObservations, {
                      id: Date.now().toString(),
                      date: '',
                      time: '',
                      bp: '',
                      pulse: '',
                      resps: '',
                      temp: '',
                      pvBleeding: '',
                      uterus: '',
                      bladder: '',
                      pallor: '',
                      babyTemp: '',
                      babyResps: '',
                      cord: '',
                      skinColour: '',
                      feedingBF: '',
                      feedingAF: '',
                      eliminationBowel: '',
                      eliminationUrine: '',
                      sign: ''
                    }]
                  })} data-id="element-8479" data-mp="3sq4gbka3lk2t">
                      + Add Observation
                    </Button>
                  </Card>

                  <Card title="Drug Chart" data-id="element-8480" data-mp="3e1r6cvrl4ehe">
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-8481" data-mp="3rzxsxfr32s1r">
                      <Input label="Ward" value={selectedMaternityFile?.drugChart?.ward || ''} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile!,
                      drugChart: {
                        ...(selectedMaternityFile?.drugChart || {
                          ward: '',
                          patientNo: '',
                          supplyDates: ['', '', ''],
                          rows: [],
                          signedBy: '',
                          signedDate: ''
                        }),
                        ward: e.target.value
                      }
                    })} data-id="element-8482" data-mp="3rx3atuvjljbi" />
                      <Input label="Patient No." value={selectedMaternityFile?.drugChart?.patientNo || ''} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile!,
                      drugChart: {
                        ...(selectedMaternityFile?.drugChart || {
                          ward: '',
                          patientNo: '',
                          supplyDates: ['', '', ''],
                          rows: [],
                          signedBy: '',
                          signedDate: ''
                        }),
                        patientNo: e.target.value
                      }
                    })} data-id="element-8483" data-mp="14ovsr54opl43" />
                    </div>
                    <div className="overflow-x-auto" data-id="element-8484" data-mp="3axl7n0pbvjc6">
                      <table className="w-full text-xs text-center border-collapse border border-gray-300 min-w-[1200px]" data-id="element-8485" data-mp="35ztj8gnw6fcq">
                        <thead className="bg-gray-50" data-id="element-8486" data-mp="31zjch1s9zkwt">
                          <tr data-id="element-8487" data-mp="148q4bf6yyemd">
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-8488" data-mp="148zvh63r2rtn">
                              Date
                            </th>
                            <th className="p-1 border border-gray-300 w-32" rowSpan={3} data-id="element-8489" data-mp="2i2nenb3i1f84">
                              Drug
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-8490" data-mp="2vwo3nd4nfnva">
                              Dosage
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-8491" data-mp="2458k5rye2h9a">
                              Route of Admin
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-8492" data-mp="3jmigvvsvreg2">
                              Ordered By
                            </th>
                            <th className="p-1 border border-gray-300" colSpan={21} data-id="element-8493" data-mp="jjbrfru2lb94">
                              SUPPLIED BY (DATE)
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-8494" data-mp="eph145jyly12">
                              Supplied By
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-8495" data-mp="4pzzbz7tw4ad">
                              Quantity
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-8496" data-mp="1l7m7vrgyga7a">
                              Date
                            </th>
                          </tr>
                          <tr data-id="element-8497" data-mp="35p57x8q1huzl">
                            {[0, 1, 2].map(colIdx => <th key={colIdx} className="p-1 border border-gray-300" colSpan={7} data-id="element-8498" data-mp="3kd9kduyi1a9x">
                                <input type="date" className="w-full border-gray-300 rounded-sm text-xs" value={selectedMaternityFile?.drugChart?.supplyDates?.[colIdx] || ''} onChange={e => {
                              const newDates = [...(selectedMaternityFile?.drugChart?.supplyDates || ['', '', ''])] as [string, string, string];
                              newDates[colIdx] = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...(selectedMaternityFile!.drugChart || {
                                    ward: '',
                                    patientNo: '',
                                    supplyDates: ['', '', ''],
                                    rows: [],
                                    signedBy: '',
                                    signedDate: ''
                                  }),
                                  supplyDates: newDates
                                }
                              });
                            }} data-id="element-8499" data-mp="3td6dc8rx8vas" />
                              </th>)}
                          </tr>
                          <tr data-id="element-8500" data-mp="1epsx5di118o2">
                            {[0, 1, 2].map(colIdx => <Fragment key={`times-${colIdx}`} data-id="element-8501" data-mp="19rhmj0jwqu4s">
                                {['6', '10', '12', '14', '18', '22', '24'].map(time => <th key={`${colIdx}-${time}`} className="p-1 border border-gray-300 w-8 font-normal" data-id="element-8502" data-mp="22g6sbcvwrnmv">
                                      {time}
                                    </th>)}
                              </Fragment>)}
                          </tr>
                        </thead>
                        <tbody data-id="element-8503" data-mp="25whzfsyqg9y6">
                          {(selectedMaternityFile?.drugChart?.rows || []).map((row, rowIndex) => <tr key={row.id} data-id="element-8504" data-mp="1vylg4dvn68oz" data-mpi={rowIndex}>
                                <td className="p-0 border border-gray-300" data-id="element-8505" data-mp="1vzvhbbxsjwzu">
                                  <input type="date" className="w-full border-0 text-xs p-1" value={row.date} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].date = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-8506" data-mp="20x55ypdcw4mm" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-8507" data-mp="hyh0rvodnk41">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.drug} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].drug = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-8508" data-mp="czvylqpkeqcy" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-8509" data-mp="lcjjt58eje8d">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.dosage} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].dosage = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-8510" data-mp="laf3fntbgmr7" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-8511" data-mp="2pggmt60q6qqa">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.routeOfAdmin} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].routeOfAdmin = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-8512" data-mp="2kix8y77xwfyv" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-8513" data-mp="2n4r5jr1k1brx">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.orderedBy} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].orderedBy = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-8514" data-mp="2n6u08odnjt2z" />
                                </td>
                                {[0, 1, 2].map(colIdx => <Fragment key={`admin-${colIdx}`} data-id="element-8515" data-mp="lzjlk37wvv8h">
                                    {['6', '10', '12', '14', '18', '22', '24'].map(time => {
                              const key = `${colIdx}-${time}`;
                              return <td key={key} className="p-0 border border-gray-300" data-id="element-8516" data-mp="1eo7965g4uwp7">
                                          <input type="text" className="w-full border-0 text-xs p-1 text-center" value={row.administration?.[key] || ''} onChange={e => {
                                  const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                                  newRows[rowIndex].administration = {
                                    ...newRows[rowIndex].administration,
                                    [key]: e.target.value
                                  };
                                  setSelectedMaternityFile({
                                    ...selectedMaternityFile!,
                                    drugChart: {
                                      ...selectedMaternityFile!.drugChart!,
                                      rows: newRows
                                    }
                                  });
                                }} data-id="element-8517" data-mp="22c450jtcc0n1" />
                                        </td>;
                            })}
                                  </Fragment>)}
                                <td className="p-0 border border-gray-300" data-id="element-8518" data-mp="1lrxaad4ro5cn">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.suppliedBy} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].suppliedBy = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-8519" data-mp="1qqhz7gekj2ls" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-8520" data-mp="2ohn3o6eulu8u">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.quantity} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].quantity = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-8521" data-mp="2kh36j2u9yqmp" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-8522" data-mp="1hpiffmr3zyyo">
                                  <input type="date" className="w-full border-0 text-xs p-1" value={row.suppliedDate} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].suppliedDate = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-8523" data-mp="1mmiqkc2tfrwn" />
                                </td>
                                <td className="p-1 border border-gray-300" data-id="element-8524" data-mp="169nkulmxku2x">
                                  <button type="button" onClick={() => {
                              const newRows = selectedMaternityFile!.drugChart!.rows.filter((_, i) => i !== rowIndex);
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} className="text-red-500 hover:text-red-700" data-id="element-8525" data-mp="16bi47vik0e4w">
                                    <Trash2 className="w-4 h-4" data-id="element-8526" data-mp="346dt7of13y28" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => {
                    const currentChart = selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    };
                    setSelectedMaternityFile({
                      ...selectedMaternityFile!,
                      drugChart: {
                        ...currentChart,
                        rows: [...currentChart.rows, {
                          id: `drug-${Date.now()}`,
                          date: '',
                          drug: '',
                          dosage: '',
                          routeOfAdmin: '',
                          orderedBy: '',
                          administration: {},
                          suppliedBy: '',
                          quantity: '',
                          suppliedDate: ''
                        }]
                      }
                    });
                  }} data-id="element-8527" data-mp="14jp8pmcswx1s">
                      + Add Drug Row
                    </Button>
                    <div className="grid grid-cols-2 gap-4 mt-6" data-id="element-8528" data-mp="1gu6umkhxox88">
                      <Input label="Date" type="date" value={selectedMaternityFile?.drugChart?.signedDate || ''} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile!,
                      drugChart: {
                        ...(selectedMaternityFile?.drugChart || {
                          ward: '',
                          patientNo: '',
                          supplyDates: ['', '', ''],
                          rows: [],
                          signedBy: '',
                          signedDate: ''
                        }),
                        signedDate: e.target.value
                      }
                    })} data-id="element-8529" data-mp="1gvrmg2cv622e" />
                      <Input label="Signature" value={selectedMaternityFile?.drugChart?.signedBy || ''} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile!,
                      drugChart: {
                        ...(selectedMaternityFile?.drugChart || {
                          ward: '',
                          patientNo: '',
                          supplyDates: ['', '', ''],
                          rows: [],
                          signedBy: '',
                          signedDate: ''
                        }),
                        signedBy: e.target.value
                      }
                    })} data-id="element-8530" data-mp="1cyu6zq1m793d" />
                    </div>
                  </Card>

                  <Card title="Puerperium Notes" data-id="element-8531" data-mp="3l7sbb5m1dl9k">
                    <div className="overflow-x-auto" data-id="element-8532" data-mp="3lamwizcvja41">
                      <table className="w-full text-sm text-left" data-id="element-8533" data-mp="3h5x1fcq1hzk6">
                        <thead className="bg-gray-50" data-id="element-8534" data-mp="29t51qpd9tktn">
                          <tr data-id="element-8535" data-mp="29uy6u088jwzj">
                            <th className="p-2 border w-32" data-id="element-8536" data-mp="25rt8m9v6uhgp">Date</th>
                            <th className="p-2 border" data-id="element-8537" data-mp="25qi4g8xtz7p2">Notes</th>
                            <th className="p-2 border w-48" data-id="element-8538" data-mp="278p70rvsfj6c">Signature</th>
                            <th className="p-2 border w-12" data-id="element-8539" data-mp="2c7s1jie56702"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-8540" data-mp="1xbkkgqy15lp0">
                          {selectedMaternityFile.puerperiumNotes.map((note, index) => <tr key={note.id} data-id="element-8541" data-mp="22bp1qj6rikga" data-mpi={index}>
                                <td className="p-1 border" data-id="element-8542" data-mp="1ohfv6byjoibl">
                                  <Input type="date" value={note.date} onChange={e => {
                              const newNotes = [...selectedMaternityFile.puerperiumNotes];
                              newNotes[index].date = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                puerperiumNotes: newNotes
                              });
                            }} data-id="element-8543" data-mp="1t97d5y3mxfq4" />
                                </td>
                                <td className="p-1 border" data-id="element-8544" data-mp="uyrgkfirn4qk">
                                  <textarea className="w-full p-1 border rounded" rows={2} value={note.notes} onChange={e => {
                              const newNotes = [...selectedMaternityFile.puerperiumNotes];
                              newNotes[index].notes = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                puerperiumNotes: newNotes
                              });
                            }} data-id="element-8545" data-mp="ux6kumkptfr4" />
                                </td>
                                <td className="p-1 border" data-id="element-8546" data-mp="2giwje09i1kv6">
                                  <Input value={note.signature} onChange={e => {
                              const newNotes = [...selectedMaternityFile.puerperiumNotes];
                              newNotes[index].signature = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                puerperiumNotes: newNotes
                              });
                            }} data-id="element-8547" data-mp="2lgethtz9664r" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-8548" data-mp="12k33x6sk7jw2">
                                  <button onClick={() => {
                              const newNotes = selectedMaternityFile.puerperiumNotes.filter((_, i) => i !== index);
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                puerperiumNotes: newNotes
                              });
                            }} className="text-red-500 hover:text-red-700" data-id="element-8549" data-mp="xk8bgyt4ym8w">
                                    <Trash2 className="w-4 h-4" data-id="element-8550" data-mp="244zswt8bju1k" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    puerperiumNotes: [...selectedMaternityFile.puerperiumNotes, {
                      id: Date.now().toString(),
                      date: '',
                      notes: '',
                      signature: ''
                    }]
                  })} data-id="element-8551" data-mp="3jqlde6dysci5">
                      + Add Note
                    </Button>
                  </Card>

                    </>}

                  {maternitySection === 'discharge' && <DischargeSlipSection file={selectedMaternityFile} onChange={setSelectedMaternityFile} onSave={handleSaveMaternityDraft} data-id="element-8552" data-mp="2jidgf0ikgjge">
                  <Card title="Discharge Slip Details" data-id="element-8553" data-mp="2thcghup9n6rs">
                    {(() => {
                    const defaultSlip = {
                      smNo: '',
                      gravida: '',
                      patientName: '',
                      party: '',
                      timeOfDischarge: '',
                      age: '',
                      motherDob: '',
                      modeOfDelivery: '',
                      perineum: '',
                      comments: '',
                      pca: '',
                      infantTob: '',
                      apgarScore: '',
                      infantSex: '',
                      dateOfDischarge: '',
                      mother: {
                        bp: '',
                        pallor: '',
                        breast: '',
                        breastfeeding: '',
                        uterus: '',
                        perineum: '',
                        lochia: '',
                        bowels: '',
                        urine: '',
                        familyPlanning: '',
                        dangerSigns: '',
                        treatment: '',
                        vitaminA: ''
                      },
                      infant: {
                        birthWeight: '',
                        dischargeWeight: '',
                        pallor: '',
                        jaundice: '',
                        passedMeconiumUrine: '',
                        feeding: '',
                        feedingOption: '',
                        breastFeeding: '',
                        bcg: '',
                        opv: '',
                        birthRecord: '',
                        gc: '',
                        treatment: ''
                      },
                      sixDaysPostnatalDate: '',
                      sixWeeksPostnatalDate: '',
                      nameInFull: ''
                    };
                    const slip = selectedMaternityFile.dischargeSlip || defaultSlip;
                    const updateSlip = (patch: Partial<typeof defaultSlip>) => {
                      setSelectedMaternityFile({
                        ...selectedMaternityFile,
                        dischargeSlip: {
                          ...slip,
                          ...patch
                        }
                      });
                    };
                    const updateMother = (patch: Partial<typeof defaultSlip.mother>) => {
                      updateSlip({
                        mother: {
                          ...slip.mother,
                          ...patch
                        }
                      });
                    };
                    const updateInfant = (patch: Partial<typeof defaultSlip.infant>) => {
                      updateSlip({
                        infant: {
                          ...slip.infant,
                          ...patch
                        }
                      });
                    };
                    return <div className="space-y-6" data-id="element-8554" data-mp="33wbxi8x7xivu">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8555" data-mp="33v0rs3k7q123">
                            <Input label="SM No" value={slip.smNo} onChange={e => updateSlip({
                          smNo: e.target.value
                        })} data-id="element-8556" data-mp="14k95296m9e61" />
                            <Input label="Gravida" value={slip.gravida} onChange={e => updateSlip({
                          gravida: e.target.value
                        })} data-id="element-8557" data-mp="3dyxqvjjs2ztg" />
                            <Input label="Patient's Name" value={slip.patientName} onChange={e => updateSlip({
                          patientName: e.target.value
                        })} data-id="element-8558" data-mp="kkmuzv7g4hx" />
                            <Input label="Party" value={slip.party} onChange={e => updateSlip({
                          party: e.target.value
                        })} data-id="element-8559" data-mp="2htdtdur7f1c0" />
                            <Input label="Time of Discharge" type="time" value={slip.timeOfDischarge} onChange={e => updateSlip({
                          timeOfDischarge: e.target.value
                        })} data-id="element-8560" data-mp="2dtn7a4cmh9dt" />
                            <Input label="Age" value={slip.age} onChange={e => updateSlip({
                          age: e.target.value
                        })} data-id="element-8561" data-mp="1g3iis6lx2lgi" />
                          </div>

                          <div data-id="element-8562" data-mp="1bfj1jynh3uqw">
                            <h4 className="font-semibold text-sm uppercase mb-2 border-b pb-1" data-id="element-8563" data-mp="1bgkfqbzscxcu">
                              Labour and Delivery Summary
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8564" data-mp="1kemkn2mn4a5e">
                              <div data-id="element-8565" data-mp="1ffta9j2ngz9g">
                                <h5 className="font-semibold mb-2 text-pink-600" data-id="element-8566" data-mp="1ferxna5zk4pm">
                                  Mother
                                </h5>
                                <div className="space-y-3" data-id="element-8567" data-mp="1p7p76qt02jc3">
                                  <Input label="D.O.B" type="date" value={slip.motherDob} onChange={e => updateSlip({
                                motherDob: e.target.value
                              })} data-id="element-8568" data-mp="236f5uavgwr2k" />
                                  <Input label="M.O.D (Mode of Delivery)" value={slip.modeOfDelivery} onChange={e => updateSlip({
                                modeOfDelivery: e.target.value
                              })} data-id="element-8569" data-mp="6ac5dvtk14k3" />
                                  <Input label="Perineum" value={slip.perineum} onChange={e => updateSlip({
                                perineum: e.target.value
                              })} data-id="element-8570" data-mp="3np95dfqysxg6" />
                                  <div data-id="element-8571" data-mp="2jh22e7mg36vj">
                                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8572" data-mp="2en65l8hx3q1y">
                                      Comments
                                    </label>
                                    <textarea className="w-full p-2 border rounded text-sm" rows={2} value={slip.comments} onChange={e => updateSlip({
                                  comments: e.target.value
                                })} data-id="element-8573" data-mp="24qbbr2wladu0" />
                                  </div>
                                  <Input label="P.C.A" value={slip.pca} onChange={e => updateSlip({
                                pca: e.target.value
                              })} data-id="element-8574" data-mp="83ae03dfpwm3" />
                                </div>
                              </div>
                              <div data-id="element-8575" data-mp="vsuyiqp8l1xh">
                                <h5 className="font-semibold mb-2 text-pink-600" data-id="element-8576" data-mp="vt4my9umv112">
                                  Infant
                                </h5>
                                <div className="space-y-3" data-id="element-8577" data-mp="hvg7jhjc4m3e">
                                  <Input label="T.O.B (Time of Birth)" value={slip.infantTob} onChange={e => updateSlip({
                                infantTob: e.target.value
                              })} data-id="element-8578" data-mp="mqloe1xvrakm" />
                                  <Input label="A/S (Apgar Score)" value={slip.apgarScore} onChange={e => updateSlip({
                                apgarScore: e.target.value
                              })} data-id="element-8579" data-mp="38zb0v1m508ww" />
                                  <Input label="Sex" value={slip.infantSex} onChange={e => updateSlip({
                                infantSex: e.target.value
                              })} data-id="element-8580" data-mp="2q29tja541wps" />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div data-id="element-8581" data-mp="1gzwf0419hn7s">
                            <div className="flex justify-between items-end border-b pb-1 mb-2" data-id="element-8582" data-mp="1gyut14ipjgba">
                              <h4 className="font-semibold text-sm uppercase" data-id="element-8583" data-mp="1m0tuu9ysu564">
                                Discharge Summary
                              </h4>
                              <div className="w-64" data-id="element-8584" data-mp="1uz5jw7k0ouwg">
                                <Input label="Date of Discharge" type="date" value={slip.dateOfDischarge} onChange={e => updateSlip({
                              dateOfDischarge: e.target.value
                            })} data-id="element-8585" data-mp="1uxuksl1iobbo" />
                              </div>
                            </div>

                            <div className="bg-pink-50 p-3 rounded mb-4" data-id="element-8586" data-mp="3u5uegzgzvhqb">
                              <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-8587" data-mp="3p79duyxtyw1c">
                                Mother
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-8588" data-mp="3kassgci0mvry">
                                <Input label="BP" value={slip.mother.bp} onChange={e => updateMother({
                              bp: e.target.value
                            })} data-id="element-8589" data-mp="lqar2h308tj6" />
                                <Input label="Pallor" value={slip.mother.pallor} onChange={e => updateMother({
                              pallor: e.target.value
                            })} data-id="element-8590" data-mp="14jn144bk0ttq" />
                                <Input label="Breast" value={slip.mother.breast} onChange={e => updateMother({
                              breast: e.target.value
                            })} data-id="element-8591" data-mp="26etqkeme4b0i" />
                                <div data-id="element-8592" data-mp="2p4rymuq6u445">
                                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8593" data-mp="2p5tjts0x46zl">
                                    Breastfeeding
                                  </label>
                                  <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={slip.mother.breastfeeding} onChange={e => updateMother({
                                breastfeeding: e.target.value
                              })} data-id="element-8594" data-mp="2y7azxaebs8oa">
                                    <option value="" data-id="element-8595" data-mp="2yzz1gkpg92vd">—</option>
                                    <option value="Yes" data-id="element-8596" data-mp="33t20379332if">Yes</option>
                                    <option value="No" data-id="element-8597" data-mp="2p3dye4wzq1u6">No</option>
                                  </select>
                                </div>
                                <Input label="Uterus" value={slip.mother.uterus} onChange={e => updateMother({
                              uterus: e.target.value
                            })} data-id="element-8598" data-mp="2u2yzakvq0k9j" />
                                <Input label="Perineum" value={slip.mother.perineum} onChange={e => updateMother({
                              perineum: e.target.value
                            })} data-id="element-8599" data-mp="lshcd8267kye" />
                                <Input label="Lochia" value={slip.mother.lochia} onChange={e => updateMother({
                              lochia: e.target.value
                            })} data-id="element-8600" data-mp="10jgkkhz3g7hg" />
                                <Input label="Bowels" value={slip.mother.bowels} onChange={e => updateMother({
                              bowels: e.target.value
                            })} data-id="element-8601" data-mp="1vtd28xlfgxtl" />
                                <Input label="Urine" value={slip.mother.urine} onChange={e => updateMother({
                              urine: e.target.value
                            })} data-id="element-8602" data-mp="2ei9xp4sk9wde" />
                                <Input label="Family Planning" value={slip.mother.familyPlanning} onChange={e => updateMother({
                              familyPlanning: e.target.value
                            })} data-id="element-8603" data-mp="u514eq2l8av4" />
                                <div className="md:col-span-2" data-id="element-8604" data-mp="7t805bkdfutd">
                                  <Input label="Danger Signs" value={slip.mother.dangerSigns} onChange={e => updateMother({
                                dangerSigns: e.target.value
                              })} data-id="element-8605" data-mp="7rn7jrhmaly5" />
                                </div>
                                <div className="md:col-span-2" data-id="element-8606" data-mp="2xgyvc1r491tu">
                                  <Input label="Treatment" value={slip.mother.treatment} onChange={e => updateMother({
                                treatment: e.target.value
                              })} data-id="element-8607" data-mp="2six9xa67wqf7" />
                                </div>
                                <Input label="Vitamin A" value={slip.mother.vitaminA} onChange={e => updateMother({
                              vitaminA: e.target.value
                            })} data-id="element-8608" data-mp="1qs5wvxa1mujr" />
                              </div>
                            </div>

                            <div className="bg-blue-50 p-3 rounded mb-4" data-id="element-8609" data-mp="bbxb7xwkijup">
                              <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-8610" data-mp="2d0jp99x651hz">
                                Infant
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-8611" data-mp="282i6kq3nn471">
                                <Input label="BWT (Birth Weight)" value={slip.infant.birthWeight} onChange={e => updateInfant({
                              birthWeight: e.target.value
                            })} data-id="element-8612" data-mp="239oyptcw3fqq" />
                                <Input label="Discharge Weight" value={slip.infant.dischargeWeight} onChange={e => updateInfant({
                              dischargeWeight: e.target.value
                            })} data-id="element-8613" data-mp="1gak66lje6kl5" />
                                <Input label="Pallor" value={slip.infant.pallor} onChange={e => updateInfant({
                              pallor: e.target.value
                            })} data-id="element-8614" data-mp="11elzvy1imlam" />
                                <Input label="Jaundice" value={slip.infant.jaundice} onChange={e => updateInfant({
                              jaundice: e.target.value
                            })} data-id="element-8615" data-mp="ihb2kizg21xt" />
                                <div className="md:col-span-2" data-id="element-8616" data-mp="1ekc6e5bowshh">
                                  <Input label="Passed Meconium / Urine" value={slip.infant.passedMeconiumUrine} onChange={e => updateInfant({
                                passedMeconiumUrine: e.target.value
                              })} data-id="element-8617" data-mp="1ekkihuzzntrm" />
                                </div>
                                <Input label="Feeding" value={slip.infant.feeding} onChange={e => updateInfant({
                              feeding: e.target.value
                            })} data-id="element-8618" data-mp="1se3dz85v22ak" />
                                <Input label="Feeding Option" value={slip.infant.feedingOption} onChange={e => updateInfant({
                              feedingOption: e.target.value
                            })} data-id="element-8619" data-mp="3txl63jmt5u40" />
                                <Input label="Breast Feeding" value={slip.infant.breastFeeding} onChange={e => updateInfant({
                              breastFeeding: e.target.value
                            })} data-id="element-8620" data-mp="3a7sqmz16yct8" />
                                <Input label="BCG" value={slip.infant.bcg} onChange={e => updateInfant({
                              bcg: e.target.value
                            })} data-id="element-8621" data-mp="14gdxb2ii17zu" />
                                <Input label="OPV" value={slip.infant.opv} onChange={e => updateInfant({
                              opv: e.target.value
                            })} data-id="element-8622" data-mp="230xon1d9ulp4" />
                                <Input label="Birth Record" value={slip.infant.birthRecord} onChange={e => updateInfant({
                              birthRecord: e.target.value
                            })} data-id="element-8623" data-mp="2lr71r6aljxvl" />
                                <Input label="G/C" value={slip.infant.gc} onChange={e => updateInfant({
                              gc: e.target.value
                            })} data-id="element-8624" data-mp="xtxxigi3qzq5" />
                                <div className="md:col-span-2" data-id="element-8625" data-mp="f1llt35k6m3c">
                                  <Input label="Treatment" value={slip.infant.treatment} onChange={e => updateInfant({
                                treatment: e.target.value
                              })} data-id="element-8626" data-mp="ttdx9428ffzs" />
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-8627" data-mp="utxp6idw4azv">
                              <Input label="6 days Postnatal Date" type="date" value={slip.sixDaysPostnatalDate} onChange={e => updateSlip({
                            sixDaysPostnatalDate: e.target.value
                          })} data-id="element-8628" data-mp="uw0f72f1m44l" />
                              <Input label="6 Weeks Postnatal Date" type="date" value={slip.sixWeeksPostnatalDate} onChange={e => updateSlip({
                            sixWeeksPostnatalDate: e.target.value
                          })} data-id="element-8629" data-mp="h3kew9dj0k7d" />
                              <div className="md:col-span-2" data-id="element-8630" data-mp="3382upzbckcay">
                                <Input label="Name in Full" value={slip.nameInFull} onChange={e => updateSlip({
                              nameInFull: e.target.value
                            })} data-id="element-8631" data-mp="2yb3y9a47nt9l" />
                              </div>
                            </div>
                          </div>
                        </div>;
                  })()}
                  </Card>
                    </DischargeSlipSection>}

                  {maternitySection === 'postnatal' && <>
                  {(() => {
                  const defaultVisit = {
                    date: '',
                    mother: {
                      generalWellBeing: '',
                      temp: '',
                      pulse: '',
                      bp: '',
                      urine: '',
                      head: '',
                      eyes: '',
                      ears: '',
                      mouth: '',
                      neck: '',
                      breasts: '',
                      ampills: '',
                      abdomenUterus: '',
                      groin: '',
                      perineum: '',
                      legs: '',
                      anus: '',
                      lochia: '',
                      cervixPerSpeculum: '',
                      familyPlanning: '',
                      treatmentOrAdvise: ''
                    },
                    infant: {
                      name: '',
                      weight: '',
                      date: '',
                      headCircumference: '',
                      length: '',
                      temp: '',
                      head: '',
                      eyes: '',
                      ears: '',
                      mouth: '',
                      neck: '',
                      chest: '',
                      abdomen: '',
                      cord: '',
                      genitaliaRectum: '',
                      extremities: '',
                      back: '',
                      neurologicExam: '',
                      feeding: '',
                      additionalNotes: ''
                    }
                  };
                  const motherFieldOrder: Array<[keyof typeof defaultVisit.mother, string]> = [['generalWellBeing', 'General Well Being'], ['temp', 'Temp'], ['pulse', 'Pulse'], ['bp', 'BP'], ['urine', 'Urine'], ['head', 'Head'], ['eyes', 'Eyes'], ['ears', 'Ears'], ['mouth', 'Mouth'], ['neck', 'Neck'], ['breasts', 'Breasts'], ['ampills', 'Ampills'], ['abdomenUterus', 'Abdomen (Uterus)'], ['groin', 'Groin'], ['perineum', 'Perineum'], ['legs', 'Legs'], ['anus', 'Anus'], ['lochia', 'Lochia']];
                  const infantFieldOrder: Array<[keyof typeof defaultVisit.infant, string]> = [['headCircumference', 'Head Circumference'], ['length', 'Length'], ['temp', 'Temp'], ['head', 'Head'], ['eyes', 'Eyes'], ['ears', 'Ears'], ['mouth', 'Mouth'], ['neck', 'Neck'], ['chest', 'Chest'], ['abdomen', 'Abdomen'], ['cord', 'Cord'], ['genitaliaRectum', 'Genitalia & Rectum'], ['extremities', 'Extremities'], ['back', 'Back'], ['neurologicExam', 'Neurologic Exam'], ['feeding', 'Feeding']];
                  const renderVisitForm = (visitKey: 'postNatalOneWeek' | 'postNatalSixWeeks') => {
                    const visit = (selectedMaternityFile as any)[visitKey] || defaultVisit;
                    const updateVisit = (patch: Partial<typeof defaultVisit>) => {
                      setSelectedMaternityFile({
                        ...selectedMaternityFile,
                        [visitKey]: {
                          ...visit,
                          ...patch
                        }
                      });
                    };
                    const updateMother = (patch: Partial<typeof defaultVisit.mother>) => {
                      updateVisit({
                        mother: {
                          ...visit.mother,
                          ...patch
                        }
                      });
                    };
                    const updateInfant = (patch: Partial<typeof defaultVisit.infant>) => {
                      updateVisit({
                        infant: {
                          ...visit.infant,
                          ...patch
                        }
                      });
                    };
                    return <div className="space-y-6" data-id="element-8632" data-mp="dly6c04qb284">
                          <Input label="Date" type="date" value={visit.date} onChange={e => updateVisit({
                        date: e.target.value
                      })} data-id="element-8633" data-mp="dnixdfru4318" />

                          <div className="bg-pink-50 p-3 rounded" data-id="element-8634" data-mp="3qy87v0ayqby1">
                            <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-8635" data-mp="3m06t2rkkkysd">
                              Mother
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-8636" data-mp="11azhvw8uythm">
                              {motherFieldOrder.map(([key, label]) => <Input key={key} label={label} value={visit.mother[key]} onChange={e => updateMother({
                            [key]: e.target.value
                          } as any)} data-id="element-8637" data-mp="2qd8zcdxjmuwd" />)}
                            </div>

                            <div className="mt-4 space-y-3" data-id="element-8638" data-mp="352y4slco5l2g">
                              <h6 className="font-semibold text-sm uppercase" data-id="element-8639" data-mp="308amj489dmwb">
                                Vaginal Examination
                              </h6>
                              <div data-id="element-8640" data-mp="3sqo8o6k8fkfk">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8641" data-mp="3srpmujwjon1i">
                                  Cervix per speculum
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={2} value={visit.mother.cervixPerSpeculum} onChange={e => updateMother({
                              cervixPerSpeculum: e.target.value
                            })} data-id="element-8642" data-mp="3vbw05i71tpuq" />
                              </div>
                              <div data-id="element-8643" data-mp="ru4700h6ezuy">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8644" data-mp="rv6sttzn6c40">
                                  Family Planning
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={2} value={visit.mother.familyPlanning} onChange={e => updateMother({
                              familyPlanning: e.target.value
                            })} data-id="element-8645" data-mp="10vlj77rgqhed" />
                              </div>
                              <div data-id="element-8646" data-mp="36u1mmxyljqsg">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8647" data-mp="3ljzje2aldot9">
                                  Treatment or Advise
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={3} value={visit.mother.treatmentOrAdvise} onChange={e => updateMother({
                              treatmentOrAdvise: e.target.value
                            })} data-id="element-8648" data-mp="3ulyygp21qitk" />
                              </div>
                            </div>
                          </div>

                          <div className="bg-blue-50 p-3 rounded" data-id="element-8649" data-mp="1wxhz7oad8ds">
                            <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-8650" data-mp="5zc543yzmuav">
                              Infant Examination Form
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-id="element-8651" data-mp="3o79yjrb39b9z">
                              <Input label="Infant Name" value={visit.infant.name} onChange={e => updateInfant({
                            name: e.target.value
                          })} data-id="element-8652" data-mp="3t5l43pucozme" />
                              <Input label="Weight" value={visit.infant.weight} onChange={e => updateInfant({
                            weight: e.target.value
                          })} data-id="element-8653" data-mp="1hcmw8k9qmd32" />
                              <Input label="Date" type="date" value={visit.infant.date} onChange={e => updateInfant({
                            date: e.target.value
                          })} data-id="element-8654" data-mp="dt6q4lke0xqq" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-8655" data-mp="3m645z4bhl6fn">
                              {infantFieldOrder.map(([key, label]) => <Input key={key} label={label} value={visit.infant[key]} onChange={e => updateInfant({
                            [key]: e.target.value
                          } as any)} data-id="element-8656" data-mp="1xnjcjmv623ta" />)}
                            </div>
                            <div className="mt-3" data-id="element-8657" data-mp="1iyn2jk9jugq1">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8658" data-mp="1nxghzi8echsu">
                                Additional Notes
                              </label>
                              <textarea className="w-full p-2 border rounded text-sm" rows={3} value={visit.infant.additionalNotes} onChange={e => updateInfant({
                            additionalNotes: e.target.value
                          })} data-id="element-8659" data-mp="v8j0vifx8r1o" />
                            </div>
                          </div>
                        </div>;
                  };
                  return <PostnatalReviewSection file={selectedMaternityFile} updatedBy={currentUser?.fullName || ''} onChange={setSelectedMaternityFile} onOpenDischargeSlip={() => setMaternitySection('discharge')} sixDayForm={<Card title="Mother & Baby — 6 Day Postnatal Review" data-id="element-8661" data-mp="1ujtyfo7p6eoi">
                            {renderVisitForm('postNatalOneWeek')}
                          </Card>} sixWeekForm={<Card title="Mother & Baby — 6 Week Postnatal Review" data-id="element-8662" data-mp="3cj07difytbom">
                            {renderVisitForm('postNatalSixWeeks')}
                          </Card>} data-id="element-8660" data-mp="98kcl7ko8vi6" />;
                })()}
                  </>}
                </div>
              </MaternityErrorBoundary> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500 h-full flex flex-col items-center justify-center" data-id="element-8663" data-mp="3v9i0vp8mgbb1">
                <Baby className="w-16 h-16 text-gray-300 mb-4" data-id="element-8664" data-mp="3hhlabpmlq9gr" />
                <p className="text-lg font-medium text-gray-900" data-id="element-8665" data-mp="3mgeg0vvncw6d">
                  Select a maternity file
                </p>
                <p data-id="element-8666" data-mp="dzteg05ch5da">
                  Choose a patient from the list to view and update their
                  maternity records.
                </p>
              </div>}
          </div>
        </div>
        </div>}
    </div>;
};